import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  Plus, Search, Filter, Download, MessageCircle, Mail,
  Eye, Edit2, Trash2, RefreshCw, ChevronRight, X, Check,
  Building2, Phone, Globe, AlertCircle, TrendingUp,
  FileText, Receipt, Clock, RotateCcw, BarChart2,
  Users, DollarSign, AlertTriangle, ArrowUpRight,
  Calendar, Wallet, CheckCircle, XCircle, ChevronDown,
  Activity, Send, CreditCard, History
} from 'lucide-react';

/* ─── Tokens ─────────────────────────────────────────────────── */
const T = {
  bg:'#f8f7ff', surface:'#ffffff', border:'#e8e4f3', borderL:'#f5f3ff',
  purple:'#7c3aed', purpleL:'#8b5cf6', purpleBg:'#ede9fe', purpleText:'#6d28d9',
  text:'#1a1035', textMd:'#4a3f6b', textSm:'#8b7fb0', textXs:'#b8b3cc',
  green:'#059669', greenBg:'#d1fae5', greenBorder:'#6ee7b7',
  red:'#dc2626', redBg:'#fee2e2', redBorder:'#fca5a5',
  yellow:'#d97706', yellowBg:'#fef3c7', yellowBorder:'#fcd34d',
  orange:'#ea580c', orangeBg:'#ffedd5', orangeBorder:'#fdba74',
  blue:'#2563eb', blueBg:'#dbeafe',
  font:"'Poppins', sans-serif",
  shadow:'0 1px 4px rgba(124,58,237,0.06)',
  shadowMd:'0 4px 20px rgba(124,58,237,0.1)',
};

/* ─── Helpers ─────────────────────────────────────────────────── */
const fmtINR = n => `₹${Number(n||0).toLocaleString('en-IN',{minimumFractionDigits:2})}`;
const fmtDate = d => { if(!d) return '—'; const dt=new Date(d+'T00:00:00'); return `${String(dt.getDate()).padStart(2,'0')}-${String(dt.getMonth()+1).padStart(2,'0')}-${dt.getFullYear()}`; };
const numberToWords = (num) => {
  const a=['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const b=['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  const convert=(n)=>{
    if(n<20)return a[n];if(n<100)return b[Math.floor(n/10)]+(n%10?' '+a[n%10]:'');
    if(n<1000)return a[Math.floor(n/100)]+' Hundred'+(n%100?' '+convert(n%100):'');
    if(n<100000)return convert(Math.floor(n/1000))+' Thousand'+(n%1000?' '+convert(n%1000):'');
    return convert(Math.floor(n/100000))+' Lakh'+(n%100000?' '+convert(n%100000):'');
  };
  return convert(Math.floor(num))+' Rupees Only';
};

const STATUS_CFG = {
  paid:     { bg:T.greenBg,   border:T.greenBorder,  text:T.green,   label:'Paid' },
  partial:  { bg:T.yellowBg,  border:T.yellowBorder, text:T.yellow,  label:'Partial' },
  unpaid:   { bg:T.redBg,     border:T.redBorder,    text:T.red,     label:'Unpaid' },
  overdue:  { bg:T.orangeBg,  border:T.orangeBorder, text:T.orange,  label:'Overdue' },
  active:   { bg:T.greenBg,   border:T.greenBorder,  text:T.green,   label:'Active' },
  inactive: { bg:'#f3f4f6',   border:'#d1d5db',      text:'#6b7280', label:'Inactive' },
  deleted:  { bg:T.redBg,     border:T.redBorder,    text:T.red,     label:'Deleted' },
  draft:    { bg:'#f3f4f6',   border:'#d1d5db',      text:'#6b7280', label:'Draft' },
  sent:     { bg:T.blueBg,    border:'#93c5fd',      text:T.blue,    label:'Sent' },
  cancelled:{ bg:T.redBg,     border:T.redBorder,    text:T.red,     label:'Cancelled' },
};

const StatusBadge = ({ status }) => {
  const c = STATUS_CFG[status] || STATUS_CFG.draft;
  return <span style={{ background:c.bg, border:`1px solid ${c.border}`, color:c.text, fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:20, whiteSpace:'nowrap' }}>{c.label}</span>;
};

/* ─── Shared UI ───────────────────────────────────────────────── */
const Spinner = () => (
  <div style={{display:'flex',alignItems:'center',justifyContent:'center',padding:48}}>
    <div style={{width:28,height:28,border:'3px solid #ede9fe',borderTop:`3px solid ${T.purple}`,borderRadius:'50%',animation:'spin 0.8s linear infinite'}}/>
  </div>
);

const Btn = ({children,onClick,variant='primary',small,disabled,icon}) => {
  const v = {
    primary:{bg:T.purple,c:'#fff',b:'none',shadow:'0 2px 8px rgba(124,58,237,0.25)'},
    secondary:{bg:'#fff',c:T.textMd,b:`1.5px solid ${T.border}`,shadow:'none'},
    green:{bg:T.green,c:'#fff',b:'none',shadow:'0 2px 8px rgba(5,150,105,0.2)'},
    red:{bg:T.red,c:'#fff',b:'none',shadow:'none'},
    ghost:{bg:'transparent',c:T.textSm,b:`1px solid ${T.border}`,shadow:'none'},
    wa:{bg:'#25d366',c:'#fff',b:'none',shadow:'0 2px 8px rgba(37,211,102,0.25)'},
    mail:{bg:'#2563eb',c:'#fff',b:'none',shadow:'0 2px 8px rgba(37,99,235,0.2)'},
  }[variant]||{};
  return (
    <button onClick={onClick} disabled={disabled}
      style={{display:'flex',alignItems:'center',gap:5,background:v.bg,color:v.c,border:v.b,
        borderRadius:9,padding:small?'6px 12px':'9px 16px',fontSize:small?12:13,fontWeight:600,
        cursor:disabled?'not-allowed':'pointer',fontFamily:T.font,opacity:disabled?0.6:1,
        whiteSpace:'nowrap',boxShadow:v.shadow,transition:'all 0.15s'}}>
      {children}
    </button>
  );
};

const Modal = ({title,subtitle,onClose,children,actions,wide}) => (
  <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.35)',display:'flex',alignItems:'center',
    justifyContent:'center',zIndex:2000,backdropFilter:'blur(4px)',fontFamily:T.font,padding:20,overflowY:'auto'}}>
    <div style={{background:'#fff',border:`1px solid ${T.border}`,borderRadius:18,padding:'26px 28px',
      width:'100%',maxWidth:wide?760:500,boxShadow:T.shadowMd,maxHeight:'90vh',overflow:'auto',margin:'auto'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:18}}>
        <div>
          <div style={{fontWeight:700,fontSize:17,color:T.text}}>{title}</div>
          {subtitle&&<div style={{fontWeight:400,fontSize:13,color:T.textSm,marginTop:3}}>{subtitle}</div>}
        </div>
        <button onClick={onClose} style={{background:'#f5f3ff',border:'none',cursor:'pointer',color:T.textSm,padding:6,borderRadius:8,display:'flex'}}><X size={16}/></button>
      </div>
      {children}
      {actions&&<div style={{display:'flex',justifyContent:'flex-end',gap:9,marginTop:20}}>{actions}</div>}
    </div>
  </div>
);

const inputSt = {width:'100%',border:`1.5px solid ${T.border}`,borderRadius:9,padding:'10px 12px',fontSize:13,fontFamily:T.font,color:T.text,background:'#faf9ff',outline:'none',boxSizing:'border-box'};

/* ─── INVOICE PDF GENERATOR ───────────────────────────────────── */
const generateInvoicePDF = (inv) => {
  const client = inv.client || {};
  const services = inv.services || [];
  const w = window.open('','_blank');
  w.document.write(`<!DOCTYPE html><html><head>
  <title>Invoice ${inv.invoice_number}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
    *{margin:0;padding:0;box-sizing:border-box;}
    body{font-family:'Poppins',sans-serif;background:#fff;color:#1a1035;}
    .page{max-width:680px;margin:0 auto;background:#fff;}

    /* HEADER */
    .header{background:#1a1035;padding:20px 28px;display:flex;align-items:center;justify-content:space-between;}
    .logo-area{display:flex;align-items:center;gap:14px;}
    .logo-img{height:44px;width:auto;}
    .brand-text .name{font-size:20px;font-weight:700;color:#fff;letter-spacing:1.5px;}
    .brand-text .sub{font-size:10px;color:rgba(255,255,255,0.6);font-weight:400;margin-top:2px;letter-spacing:1px;}
    .header-right{text-align:right;}
    .header-right .doc-type{font-size:22px;font-weight:700;color:#fff;letter-spacing:2px;}
    .header-right .inv-num{font-size:11px;color:rgba(255,255,255,0.55);margin-top:4px;font-weight:400;}

    /* META ROW */
    .meta-bar{background:#f5f3ff;border-bottom:1px solid #e8e4f3;padding:12px 28px;display:flex;gap:28px;align-items:center;}
    .meta-item{display:flex;align-items:center;gap:7px;font-size:12px;color:#4a3f6b;}
    .meta-icon{width:22px;height:22px;background:#ede9fe;border-radius:6px;display:flex;align-items:center;justify-content:center;color:#7c3aed;font-size:12px;flex-shrink:0;}
    .meta-label{font-weight:500;color:#8b7fb0;margin-right:3px;}
    .meta-value{font-weight:600;color:#1a1035;}

    /* BODY */
    .body{padding:20px 28px;}

    /* PACKAGE HEADER */
    .pkg-title{text-align:center;margin:8px 0 14px;}
    .pkg-title .line{height:1.5px;background:#e8e4f3;flex:1;}
    .pkg-title .inner{display:flex;align-items:center;gap:12px;}
    .pkg-title h2{font-size:14px;font-weight:600;color:#1a1035;letter-spacing:1px;white-space:nowrap;}

    /* SERVICES BOX */
    .services-box{border:1.5px solid #e8e4f3;border-radius:10px;overflow:hidden;margin-bottom:16px;}
    .services-header{background:#f5f3ff;padding:10px 16px;display:flex;align-items:center;gap:8px;border-bottom:1px solid #e8e4f3;}
    .services-header span{font-size:11px;font-weight:600;color:#7c3aed;text-transform:uppercase;letter-spacing:1px;}
    .service-item{display:flex;align-items:center;gap:12px;padding:10px 16px;border-bottom:1px dashed #f0edfb;}
    .service-item:last-child{border-bottom:none;}
    .service-num{width:22px;height:22px;background:#7c3aed;border-radius:50%;color:#fff;font-size:10px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
    .service-text{font-size:12px;color:#1a1035;font-weight:400;}

    /* TOTAL BOX */
    .total-box{background:#f5f3ff;border:1.5px solid #e8e4f3;border-radius:10px;padding:14px 18px;display:flex;align-items:center;justify-content:space-between;margin-bottom:16px;}
    .total-label{display:flex;align-items:center;gap:10px;font-size:13px;font-weight:600;color:#1a1035;}
    .total-icon{width:32px;height:32px;background:#7c3aed;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#fff;font-size:14px;}
    .total-amount{font-size:22px;font-weight:700;color:#7c3aed;}

    /* BALANCE ROW */
    .balance-row{display:flex;justify-content:space-between;padding:6px 0;font-size:12px;border-bottom:1px solid #f5f3ff;}
    .balance-row.prev{color:#d97706;}
    .balance-row.due{font-weight:600;color:#1a1035;border-top:1.5px solid #e8e4f3;padding-top:8px;margin-top:4px;border-bottom:none;}

    /* PAYMENT SECTION */
    .payment-section{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:16px;}
    .pay-card{border:1px solid #e8e4f3;border-radius:10px;padding:12px 14px;background:#fff;}
    .pay-card-title{font-size:10px;font-weight:700;color:#8b7fb0;text-transform:uppercase;letter-spacing:1px;margin-bottom:8px;}
    .pay-row{display:flex;justify-content:space-between;font-size:11px;padding:3px 0;color:#4a3f6b;}
    .pay-row .val{font-weight:600;color:#1a1035;}

    /* QR */
    .qr-box{border:1px solid #e8e4f3;border-radius:10px;padding:12px;text-align:center;background:#fff;}
    .qr-box img{width:80px;height:80px;object-fit:contain;display:block;margin:0 auto 6px;}
    .qr-box .qr-label{font-size:9px;color:#8b7fb0;font-weight:500;}
    .qr-placeholder{width:80px;height:80px;background:#f5f3ff;border-radius:8px;margin:0 auto 6px;display:flex;align-items:center;justify-content:center;font-size:9px;color:#8b7fb0;}

    /* SIGNATURE */
    .sig-area{text-align:center;padding:10px 28px 12px;border-top:1px solid #f0edfb;}
    .sig-line{width:130px;border-top:1px solid #1a1035;margin:0 auto 5px;}
    .sig-name{font-size:12px;font-weight:600;color:#1a1035;}
    .sig-title{font-size:10px;color:#6b7280;}

    /* FOOTER */
    .footer{background:#1a1035;padding:12px 28px;display:flex;justify-content:space-around;align-items:center;}
    .footer-item{display:flex;align-items:center;gap:7px;color:rgba(255,255,255,0.7);font-size:10px;}
    .footer-icon{width:22px;height:22px;background:rgba(255,255,255,0.1);border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:10px;}
    .sys-note{text-align:center;font-size:9px;color:rgba(255,255,255,0.4);margin-top:6px;padding-bottom:10px;}

    @media print{@page{margin:8mm;size:A4;}body{padding:0;}.page{max-width:100%;}}
  </style></head><body>
  <div class="page">

    <!-- HEADER -->
    <div class="header">
      <div class="logo-area">
        <img class="logo-img" src="/reach-360.svg" alt="Reach 360" onerror="this.style.display='none'"/>
        <div class="brand-text">
          <div class="name">REACH 360</div>
          <div class="sub">MEDIA BRANDING COMPANY</div>
        </div>
      </div>
      <div class="header-right">
        <div class="doc-type">INVOICE</div>
        <div class="inv-num">${inv.invoice_number} &nbsp;|&nbsp; ${inv.client_invoice_number||''}</div>
      </div>
    </div>

    <!-- META BAR -->
    <div class="meta-bar">
      <div class="meta-item"><div class="meta-icon">👤</div><span><span class="meta-label">Client</span><span class="meta-value">${client.company_name||'—'}</span></span></div>
      <div class="meta-item"><div class="meta-icon">📄</div><span><span class="meta-label">Invoice No.</span><span class="meta-value">${inv.invoice_number}</span></span></div>
      <div class="meta-item"><div class="meta-icon">📅</div><span><span class="meta-label">Invoice Date</span><span class="meta-value">${fmtDate(inv.invoice_date)}</span></span></div>
      <div class="meta-item"><div class="meta-icon">⏰</div><span><span class="meta-label">Due Date</span><span class="meta-value">${fmtDate(inv.due_date||'')}</span></span></div>
      ${(inv.fmt_cycle_start||inv.fmt_cycle_end)?`<div class="meta-item"><div class="meta-icon">🗓</div><span><span class="meta-label">Billing Period</span><span class="meta-value">${inv.fmt_cycle_start||'—'} to ${inv.fmt_cycle_end||'—'}</span></span></div>`:''}
    </div>

    <div class="body">

      <!-- PACKAGE TITLE -->
      <div class="pkg-title">
        <div class="inner">
          <div class="line"></div>
          <h2>${(inv.package_name||'').toUpperCase()}</h2>
          <div class="line"></div>
        </div>
      </div>

      <!-- SERVICES -->
      <div class="services-box">
        <div class="services-header">
          <span>📋</span><span>Description</span>
        </div>
        ${services.map((s,i)=>`
          <div class="service-item">
            <div class="service-num">${i+1}</div>
            <div class="service-text">${s.title||s.description||s}</div>
          </div>`).join('')}
      </div>

      <!-- BALANCE BREAKDOWN -->
      ${Number(inv.previous_balance)>0?`
      <div style="margin-bottom:12px;">
        <div class="balance-row"><span>Current Period Tariff</span><span>${fmtINR(inv.tariff_amount)}</span></div>
        <div class="balance-row prev"><span>Previous Balance</span><span>+ ${fmtINR(inv.previous_balance)}</span></div>
        <div class="balance-row due"><span>Total Due</span><span>${fmtINR(inv.total_amount)}</span></div>
      </div>`:''}

      <!-- TOTAL BOX -->
      <div class="total-box">
        <div class="total-label"><div class="total-icon">₹</div>TOTAL PACKAGE COST</div>
        <div class="total-amount">${fmtINR(inv.total_amount)}</div>
      </div>

      <!-- PAYMENT DETAILS -->
      <div class="payment-section">
        <div class="pay-card">
          <div class="pay-card-title">Bank Transfer</div>
          <div class="pay-row"><span>Account Name</span><span class="val">S Kailash</span></div>
          <div class="pay-row"><span>Bank</span><span class="val">Axis Bank</span></div>
          <div class="pay-row"><span>Account No.</span><span class="val">923010051426791</span></div>
          <div class="pay-row"><span>IFSC</span><span class="val">UTIB0000006</span></div>
        </div>
        <div style="display:flex;flex-direction:column;gap:8px;">
          <div class="pay-card">
            <div class="pay-card-title">UPI / Phone Pay</div>
            <div class="pay-row"><span>GPay / PhonePe</span><span class="val">9943661843</span></div>
          </div>
          <div class="qr-box">
            <img src="/payment-qr.svg" alt="QR" onerror="this.parentElement.innerHTML='<div class=qr-placeholder>QR Code</div><div class=qr-label>Scan to Pay</div>'"/>
            <div class="qr-label">Scan to Pay</div>
          </div>
        </div>
      </div>

      <!-- SIGNATURE -->
      <div class="sig-area">
        <div class="sig-line"></div>
        <div class="sig-name">Kailash</div>
        <div class="sig-title">Founder, Reach 360</div>
      </div>
    </div>

    <!-- FOOTER -->
    <div class="footer">
      <div class="footer-item"><div class="footer-icon">📞</div>9443394439</div>
      <div class="footer-item"><div class="footer-icon">✉</div>reach360d@gmail.com</div>
      <div class="footer-item"><div class="footer-icon">🌐</div>www.reach360.in</div>
    </div>
    <div style="background:#1a1035;padding-bottom:10px;"><div class="sys-note">This is a system-generated invoice.</div></div>

  </div>
  </body></html>`);
  w.document.close();
  setTimeout(()=>w.print(),600);
};

/* ─── RECEIPT PDF GENERATOR ──────────────────────────────────── */
const generateReceiptPDF = (rcp) => {
  const client = rcp.client || {};
  const invoice = rcp.invoice || {};
  const services = (invoice.services||client.services||[]);
  const w = window.open('','_blank');
  w.document.write(`<!DOCTYPE html><html><head>
  <title>Receipt ${rcp.receipt_number}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
    *{margin:0;padding:0;box-sizing:border-box;}
    body{font-family:'Poppins',sans-serif;background:#fff;color:#1a1035;padding:0;}
    .page{max-width:640px;margin:0 auto;}

    .header{text-align:center;padding:20px 28px 14px;border-bottom:1px solid #e8e4f3;}
    .logo-img{height:48px;margin-bottom:8px;display:block;margin-left:auto;margin-right:auto;}
    .company-name{font-size:22px;font-weight:700;color:#1a1035;letter-spacing:0.5px;}
    .company-sub{font-size:11px;color:#6b7280;font-weight:400;margin-top:2px;}

    .doc-title{text-align:center;padding:14px 28px;}
    .doc-title h1{font-size:20px;font-weight:600;color:#1a1035;letter-spacing:2px;}
    .doc-title .title-line{width:50px;height:2px;background:#1a1035;margin:6px auto 0;}

    .body{padding:0 28px;}

    .info-section{padding:12px 0;border-bottom:1px solid #e8e4f3;margin-bottom:12px;}
    .info-row{display:grid;grid-template-columns:110px 10px 1fr;font-size:12px;padding:4px 0;gap:0;}
    .info-label{color:#6b7280;font-weight:400;}
    .info-colon{color:#6b7280;text-align:center;}
    .info-value{color:#1a1035;font-weight:500;}

    .section-title{font-size:11px;font-weight:700;color:#1a1035;text-transform:uppercase;letter-spacing:1px;margin:12px 0 8px;}
    .bullet-item{display:flex;align-items:flex-start;gap:8px;font-size:12px;color:#1a1035;padding:4px 0;border-bottom:1px solid #f5f3ff;}
    .bullet-item:last-child{border-bottom:none;}
    .bullet-dot{width:6px;height:6px;border-radius:50%;background:#1a1035;margin-top:4px;flex-shrink:0;}

    hr{border:none;border-top:1px solid #e8e4f3;margin:12px 0;}

    .amount-row{display:flex;justify-content:space-between;align-items:baseline;padding:5px 0;font-size:13px;}
    .amount-row .label{color:#1a1035;font-weight:500;}
    .amount-row .val{font-weight:600;color:#1a1035;}
    .amount-row .words{font-size:10px;color:#8b7fb0;font-weight:400;margin-top:1px;}
    .balance-amt{color:#dc2626 !important;}
    .status-row{display:flex;justify-content:space-between;align-items:center;padding:6px 0;font-size:13px;font-weight:600;}
    .status-paid{color:#059669;}
    .status-partial{color:#d97706;}
    .status-pending{color:#dc2626;}

    /* PAYMENT DETAILS */
    .pay-section{display:grid;grid-template-columns:1fr auto;gap:12px;margin:12px 0;}
    .pay-card{border:1px solid #e8e4f3;border-radius:8px;padding:10px 12px;}
    .pay-card-title{font-size:9px;font-weight:700;color:#8b7fb0;text-transform:uppercase;letter-spacing:1px;margin-bottom:6px;}
    .pay-row{display:flex;justify-content:space-between;font-size:10px;padding:2px 0;color:#4a3f6b;}
    .pay-row .val{font-weight:600;color:#1a1035;}
    .qr-box{border:1px solid #e8e4f3;border-radius:8px;padding:8px;text-align:center;}
    .qr-box img{width:70px;height:70px;object-fit:contain;display:block;margin:0 auto 4px;}
    .qr-label{font-size:8px;color:#8b7fb0;}

    .sig-area{text-align:center;padding:10px 0 6px;border-top:1px solid #f0edfb;margin-top:12px;}
    .sig-line{width:120px;border-top:1px solid #1a1035;margin:0 auto 4px;}
    .sig-name{font-size:11px;font-weight:600;color:#1a1035;}
    .sig-title{font-size:9px;color:#6b7280;}
    .sys-note{font-size:10px;color:#b8b3cc;text-align:center;padding:8px 0;}

    .footer{border-top:1px solid #e8e4f3;display:flex;justify-content:space-around;padding:10px 28px;}
    .footer-item{display:flex;align-items:center;gap:6px;font-size:10px;color:#4a3f6b;}

    @media print{@page{margin:8mm;size:A4;}body{padding:0;}.page{max-width:100%;}}
  </style></head><body>
  <div class="page">
    <div class="header">
      <img class="logo-img" src="/reach-360.svg" alt="Reach 360" onerror="this.style.display='none'"/>
      <div class="company-name">REACH 360</div>
      <div class="company-sub">Media Branding Company</div>
    </div>

    <div class="doc-title">
      <h1>RECEIPT</h1>
      <div class="title-line"></div>
    </div>

    <div class="body">
      <div class="info-section">
        <div class="info-row"><span class="info-label">Receipt No.</span><span class="info-colon">:</span><span class="info-value">${rcp.receipt_number}</span></div>
        <div class="info-row"><span class="info-label">Date</span><span class="info-colon">:</span><span class="info-value">${fmtDate(rcp.receipt_date)}</span></div>
      </div>

      <div class="info-section">
        <div class="info-row"><span class="info-label">Client</span><span class="info-colon">:</span><span class="info-value">${client.company_name||'—'}</span></div>
        ${invoice.invoice_number?`<div class="info-row"><span class="info-label">Invoice No.</span><span class="info-colon">:</span><span class="info-value">${invoice.invoice_number}</span></div>`:''}
        ${(rcp.fmt_cycle_start||rcp.fmt_cycle_end)?`<div class="info-row"><span class="info-label">Billing Period</span><span class="info-colon">:</span><span class="info-value">${rcp.fmt_cycle_start||'—'} to ${rcp.fmt_cycle_end||'—'}</span></div>`:''}
      </div>

      ${services.length>0?`
      <div class="section-title">Package Details</div>
      ${services.map(s=>`<div class="bullet-item"><div class="bullet-dot"></div><span>${s.title||s.description||s}</span></div>`).join('')}
      <hr/>`:''}

      <div class="amount-row">
        <div>
          <div class="label">Amount Received</div>
          <div class="words">(${numberToWords(Math.floor(Number(rcp.amount_received)))})</div>
        </div>
        <div class="val">${fmtINR(rcp.amount_received)}</div>
      </div>

      ${Number(rcp.previous_balance)>0?`
      <div class="amount-row">
        <div class="label">Previous Balance</div>
        <div class="val balance-amt">${fmtINR(rcp.previous_balance)}</div>
      </div>`:''}

      ${Number(rcp.balance_remaining)>0?`
      <div class="amount-row">
        <div class="label">Balance Remaining</div>
        <div class="val balance-amt">${fmtINR(rcp.balance_remaining)}</div>
      </div>`:''}

      <hr/>

      <div class="status-row">
        <span>Payment Status</span>
        <span class="status-${rcp.payment_status||'partial'}">${rcp.payment_status==='paid'?'PAID':rcp.payment_status==='partial'?'PARTIAL PAYMENT':'PENDING'}</span>
      </div>

      <hr/>

      <!-- PAYMENT DETAILS & QR -->
      <div class="pay-section">
        <div style="display:flex;flex-direction:column;gap:8px;">
          <div class="pay-card">
            <div class="pay-card-title">Bank Transfer</div>
            <div class="pay-row"><span>Account Name</span><span class="val">S Kailash</span></div>
            <div class="pay-row"><span>Bank</span><span class="val">Axis Bank</span></div>
            <div class="pay-row"><span>Account No.</span><span class="val">923010051426791</span></div>
            <div class="pay-row"><span>IFSC</span><span class="val">UTIB0000006</span></div>
          </div>
          <div class="pay-card">
            <div class="pay-card-title">UPI</div>
            <div class="pay-row"><span>GPay / PhonePe</span><span class="val">9943661843</span></div>
          </div>
        </div>
        <div class="qr-box">
          <img src="/payment-qr.svg" alt="QR" onerror="this.parentElement.innerHTML='<div style=width:70px;height:70px;background:#f5f3ff;border-radius:6px;margin:0 auto 4px;display:flex;align-items:center;justify-content:center;font-size:8px;color:#8b7fb0>QR Code</div><div class=qr-label>Scan to Pay</div>'"/>
          <div class="qr-label">Scan to Pay</div>
        </div>
      </div>

      <div class="sig-area">
        <div class="sig-line"></div>
        <div class="sig-name">Kailash</div>
        <div class="sig-title">Founder, Reach 360</div>
      </div>

      <div class="sys-note">This is a system-generated receipt.</div>
    </div>

    <div class="footer">
      <div class="footer-item">✉ reach360d@gmail.com</div>
      <div class="footer-item">🌐 www.reach360.in</div>
      <div class="footer-item">📞 9443394439</div>
    </div>
  </div>
  </body></html>`);
  w.document.close();
  setTimeout(()=>w.print(),600);
};

/* ─── WHATSAPP / EMAIL ──────────────────────────────────────────── */
const sendWhatsApp = (client, docType, docNumber, amount) => {
  const num = (client.whatsapp||client.phone||'').replace(/\D/g,'');
  if (!num) { toast.error('No WhatsApp number for this client'); return; }
  const msg = `Hi ${client.contact_person},\n\nPlease find your ${docType} from Reach 360.\n\n${docType} No: ${docNumber}\nAmount: ${fmtINR(amount)}\n\nFor payments:\nGPay/PhonePe: 9943661843\nBank: Axis Bank | Acc: 923010051426791 | IFSC: UTIB0000006\n\nThank you,\nKailash - Reach 360\n9443394439`;
  window.open(`https://wa.me/91${num}?text=${encodeURIComponent(msg)}`, '_blank');
};

const sendEmail = (client, docType, docNumber, amount) => {
  if (!client.email) { toast.error('No email for this client'); return; }
  const subject = `${docType} from Reach 360 - ${docNumber}`;
  const body = `Dear ${client.contact_person},\n\nPlease find your ${docType} details below.\n\n${docType} No: ${docNumber}\nAmount: ${fmtINR(amount)}\n\nPayment Details:\nGPay/PhonePe: 9943661843\nBank: Axis Bank\nAccount: 923010051426791\nIFSC: UTIB0000006\n\nThank you for your business.\n\nWarm regards,\nKailash\nFounder, Reach 360\n9443394439\nwww.reach360.in`;
  window.location.href = `mailto:${client.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

/* ─── OVERVIEW STAT CARD ─────────────────────────────────────── */
const StatCard = ({icon:Icon,label,value,sub,color,bg}) => (
  <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:14,padding:'18px 20px',boxShadow:T.shadow,flex:1,minWidth:140}}>
    <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:10}}>
      <div style={{width:40,height:40,borderRadius:11,background:bg,display:'flex',alignItems:'center',justifyContent:'center'}}>
        <Icon size={19} color={color}/>
      </div>
    </div>
    <div style={{fontWeight:700,fontSize:22,color:T.text,lineHeight:1,marginBottom:4}}>{value}</div>
    <div style={{fontWeight:500,fontSize:12,color:T.textSm}}>{label}</div>
    {sub&&<div style={{fontWeight:400,fontSize:11,color:T.textXs,marginTop:2}}>{sub}</div>}
  </div>
);


/* ─── NEW BILLING CYCLE MODAL ───────────────────────────────── */
function NewBillingCycleModal({client, cycles, onClose, onSaved}) {
  const lastCycle = cycles[0]; // most recent
  const [saving, setSaving] = useState(false);

  // Auto-suggest next cycle dates
  const suggestStart = lastCycle
    ? (() => { const d = new Date(lastCycle.cycle_end+'T00:00:00'); d.setDate(d.getDate()+1); return d.toISOString().split('T')[0]; })()
    : new Date().toISOString().split('T')[0];
  const suggestEnd = (() => {
    const d = new Date(suggestStart+'T00:00:00');
    d.setDate(d.getDate() + (client.billing_cycle_days||30));
    return d.toISOString().split('T')[0];
  })();

  const [form, setForm] = useState({
    cycle_start: suggestStart,
    cycle_end: suggestEnd,
    tariff_amount: client.tariff_amount||'',
    carry_forward_balance: lastCycle ? Math.max(0, Number(lastCycle.balance_remaining)||0) : 0,
  });

  const totalDue = Number(form.tariff_amount||0) + Number(form.carry_forward_balance||0);

  const submit = async () => {
    if (!form.cycle_start||!form.cycle_end||!form.tariff_amount)
      return toast.error('All fields required');
    setSaving(true);
    try {
      await api.post(`/clients/${client.id}/cycles`, form);
      toast.success('New billing cycle created');
      onSaved();
    } catch(e) { toast.error(e.response?.data?.error||'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="New Billing Cycle" subtitle={`${client.company_name} · Cycle ${(cycles.length||0)+1}`} onClose={onClose}
      actions={[<Btn key="c" variant="secondary" onClick={onClose}>Cancel</Btn>,<Btn key="s" onClick={submit} disabled={saving}>{saving?'Creating...':'Create Cycle'}</Btn>]}>

      {lastCycle&&Number(lastCycle.balance_remaining)>0&&(
        <div style={{background:'#fff7ed',border:'1px solid #fed7aa',borderRadius:10,padding:'11px 14px',marginBottom:16}}>
          <div style={{fontWeight:600,fontSize:13,color:'#9a3412'}}>⚠ Previous cycle has outstanding balance</div>
          <div style={{fontSize:12,color:'#92400e',marginTop:3}}>Cycle {lastCycle.cycle_number} balance: <b>{fmtINR(lastCycle.balance_remaining)}</b> — auto-added as carry forward</div>
        </div>
      )}

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
        {[['cycle_start','Cycle Start Date','date'],['cycle_end','Cycle End Date','date']].map(([k,l,t])=>(
          <div key={k}>
            <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>{l}</label>
            <input type={t} style={inputSt} value={form[k]} onChange={e=>setForm(p=>({...p,[k]:e.target.value}))}/>
          </div>
        ))}
        <div>
          <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Tariff Amount (₹)</label>
          <input type="number" style={inputSt} value={form.tariff_amount} onChange={e=>setForm(p=>({...p,tariff_amount:e.target.value}))}/>
        </div>
        <div>
          <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Carry Forward Balance (₹)</label>
          <input type="number" style={inputSt} value={form.carry_forward_balance} onChange={e=>setForm(p=>({...p,carry_forward_balance:e.target.value}))}/>
        </div>
      </div>
      <div style={{background:T.purpleBg,border:`1px solid #c4b5fd`,borderRadius:10,padding:'12px 14px',marginTop:14}}>
        <div style={{fontSize:12,color:T.purpleText,fontWeight:600,marginBottom:4}}>Cycle Summary</div>
        <div style={{fontSize:13,color:T.textMd}}>
          Period: <b>{fmtDate(form.cycle_start)} → {fmtDate(form.cycle_end)}</b>
        </div>
        <div style={{fontSize:13,color:T.textMd,marginTop:4}}>
          Tariff: <b>{fmtINR(Number(form.tariff_amount||0))}</b>
          {Number(form.carry_forward_balance)>0&&<span style={{color:T.yellow}}> + Carry Forward: <b>{fmtINR(Number(form.carry_forward_balance))}</b></span>}
        </div>
        <div style={{fontSize:14,color:T.text,fontWeight:700,marginTop:6,borderTop:`1px solid #c4b5fd`,paddingTop:6}}>
          Total Due: {fmtINR(totalDue)}
        </div>
      </div>
    </Modal>
  );
}

/* ─── ONBOARDING PDF GENERATOR ───────────────────────────────── */
const generateOnboardingPDF = (client) => {
  const services = client.services || [];
  const today = new Date().toLocaleDateString('en-IN',{day:'2-digit',month:'long',year:'numeric'});
  const billingStart = fmtDate(client.billing_start_date);
  const billingEnd = (() => {
    if (!client.billing_start_date) return '—';
    const d = new Date(client.billing_start_date+'T00:00:00');
    d.setDate(d.getDate()+(client.billing_cycle_days||30));
    return fmtDate(d.toISOString().split('T')[0]);
  })();

  const w = window.open('','_blank');
  w.document.write(`<!DOCTYPE html><html><head>
  <title>Welcome - ${client.company_name}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
    *{margin:0;padding:0;box-sizing:border-box;}
    body{font-family:'Poppins',sans-serif;background:#fff;color:#1a1035;padding:0;}
    .page{max-width:680px;margin:0 auto;}

    .header{background:#1a1035;padding:22px 32px;display:flex;align-items:center;justify-content:space-between;}
    .logo-img{height:40px;}
    .brand{color:#fff;}
    .brand .name{font-size:18px;font-weight:700;letter-spacing:1px;}
    .brand .sub{font-size:9px;color:rgba(255,255,255,0.5);font-weight:400;letter-spacing:1.5px;margin-top:2px;}
    .header-right{text-align:right;}
    .header-right .doc{font-size:13px;font-weight:600;color:rgba(255,255,255,0.7);letter-spacing:2px;}
    .header-right .date{font-size:10px;color:rgba(255,255,255,0.4);margin-top:3px;}

    .welcome-band{background:#7c3aed;padding:16px 32px;text-align:center;}
    .welcome-band h1{font-size:18px;font-weight:600;color:#fff;letter-spacing:1px;}
    .welcome-band p{font-size:11px;color:rgba(255,255,255,0.7);margin-top:4px;}

    .body{padding:22px 32px;}

    .client-card{border:1.5px solid #e8e4f3;border-radius:12px;padding:16px 18px;margin-bottom:18px;background:#faf9ff;}
    .client-card .code{font-size:10px;font-weight:700;color:#7c3aed;background:#ede9fe;padding:'2px 8px';border-radius:10;display:'inline-block';margin-bottom:8px;letter-spacing:1px;}
    .client-card .name{font-size:18px;font-weight:700;color:#1a1035;margin-bottom:10px;}
    .info-grid{display:grid;grid-template-columns:1fr 1fr;gap:6px;}
    .info-row{display:flex;gap:6px;font-size:11px;padding:4px 0;border-bottom:1px solid #f0edfb;}
    .info-row:last-child{border-bottom:none;}
    .info-label{color:#8b7fb0;font-weight:500;min-width:90px;}
    .info-value{color:#1a1035;font-weight:500;}

    .section-title{font-size:10px;font-weight:700;color:#7c3aed;text-transform:uppercase;letter-spacing:1.5px;margin:16px 0 8px;}

    .pkg-card{border:1.5px solid #e8e4f3;border-radius:10px;overflow:hidden;margin-bottom:16px;}
    .pkg-header{background:#1a1035;padding:10px 16px;display:flex;align-items:center;justify-content:space-between;}
    .pkg-name{font-size:13px;font-weight:600;color:#fff;}
    .pkg-amount{font-size:15px;font-weight:700;color:#c4b5fd;}
    .service-item{display:flex;align-items:center;gap:10px;padding:9px 16px;border-bottom:1px solid #f5f3ff;font-size:12px;color:#1a1035;}
    .service-item:last-child{border-bottom:none;}
    .service-num{width:20px;height:20px;background:#7c3aed;border-radius:50%;color:#fff;font-size:9px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0;}

    .billing-card{display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;margin-bottom:16px;}
    .billing-item{background:#f5f3ff;border:1px solid #e8e4f3;border-radius:9px;padding:10px 12px;}
    .billing-item .label{font-size:9px;color:#8b7fb0;font-weight:600;text-transform:uppercase;letter-spacing:0.8px;margin-bottom:4px;}
    .billing-item .value{font-size:13px;font-weight:600;color:#1a1035;}

    .payment-section{display:grid;grid-template-columns:1fr auto;gap:12px;margin-bottom:16px;}
    .pay-card{border:1px solid #e8e4f3;border-radius:9px;padding:11px 14px;background:#fff;}
    .pay-card-title{font-size:9px;font-weight:700;color:#8b7fb0;text-transform:uppercase;letter-spacing:1px;margin-bottom:7px;}
    .pay-row{display:flex;justify-content:space-between;font-size:10px;padding:2px 0;}
    .pay-row .val{font-weight:600;color:#1a1035;}
    .qr-box{border:1px solid #e8e4f3;border-radius:9px;padding:10px;text-align:center;background:#fff;}
    .qr-box img{width:75px;height:75px;object-fit:contain;display:block;margin:0 auto 5px;}
    .qr-label{font-size:9px;color:#8b7fb0;}

    .note-box{background:#fef3c7;border:1px solid #fcd34d;border-radius:9px;padding:11px 14px;margin-bottom:14px;}
    .note-box p{font-size:11px;color:#92400e;font-weight:500;line-height:1.6;}

    .footer{background:#1a1035;padding:12px 32px;display:flex;justify-content:space-around;}
    .footer-item{font-size:10px;color:rgba(255,255,255,0.6);display:flex;align-items:center;gap:5px;}
    .sys-note{text-align:center;background:#1a1035;font-size:9px;color:rgba(255,255,255,0.3);padding-bottom:10px;}

    @media print{@page{margin:8mm;size:A4;}body{padding:0;}.page{max-width:100%;}}
  </style></head><body>
  <div class="page">
    <div class="header">
      <div style="display:flex;align-items:center;gap:12px;">
        <img class="logo-img" src="/reach-360.svg" alt="Reach 360" onerror="this.style.display='none'"/>
        <div class="brand"><div class="name">REACH 360</div><div class="sub">MEDIA BRANDING COMPANY</div></div>
      </div>
      <div class="header-right">
        <div class="doc">CLIENT ONBOARDING</div>
        <div class="date">${today}</div>
      </div>
    </div>

    <div class="welcome-band">
      <h1>Welcome Aboard, ${client.company_name}!</h1>
      <p>Thank you for choosing Reach 360. Here are your onboarding details.</p>
    </div>

    <div class="body">

      <div class="client-card">
        <div style="font-size:10px;font-weight:700;color:#7c3aed;background:#ede9fe;padding:2px 8px;border-radius:10px;display:inline-block;margin-bottom:8px;letter-spacing:1px;">${client.client_code}</div>
        <div class="name">${client.company_name}</div>
        <div class="info-grid">
          <div>
            ${client.contact_person?`<div class="info-row"><span class="info-label">Contact Person</span><span class="info-value">${client.contact_person}</span></div>`:''}
            ${client.phone?`<div class="info-row"><span class="info-label">Phone</span><span class="info-value">${client.phone}</span></div>`:''}
            ${client.email?`<div class="info-row"><span class="info-label">Email</span><span class="info-value">${client.email}</span></div>`:''}
          </div>
          <div>
            ${client.city?`<div class="info-row"><span class="info-label">City</span><span class="info-value">${client.city}</span></div>`:''}
            ${client.gst_number?`<div class="info-row"><span class="info-label">GST No.</span><span class="info-value">${client.gst_number}</span></div>`:''}
            ${client.address?`<div class="info-row"><span class="info-label">Address</span><span class="info-value">${client.address}</span></div>`:''}
          </div>
        </div>
      </div>

      <div class="section-title">Package & Services</div>
      <div class="pkg-card">
        <div class="pkg-header">
          <span class="pkg-name">${client.package_name}</span>
          <span class="pkg-amount">${fmtINR(client.tariff_amount)}/cycle</span>
        </div>
        ${services.map((s,i)=>`<div class="service-item"><div class="service-num">${i+1}</div><span>${s.title||s.description||s}</span></div>`).join('')}
      </div>

      <div class="section-title">Billing Information</div>
      <div class="billing-card">
        <div class="billing-item"><div class="label">Billing Cycle</div><div class="value">${client.billing_cycle_days} Days</div></div>
        <div class="billing-item"><div class="label">First Cycle Start</div><div class="value">${billingStart}</div></div>
        <div class="billing-item"><div class="label">First Cycle End</div><div class="value">${billingEnd}</div></div>
        <div class="billing-item"><div class="label">Tariff Amount</div><div class="value" style="color:#7c3aed">${fmtINR(client.tariff_amount)}</div></div>
        ${Number(client.previous_balance)>0?`<div class="billing-item"><div class="label">Carry Forward</div><div class="value" style="color:#d97706">${fmtINR(client.previous_balance)}</div></div>`:''}
        <div class="billing-item"><div class="label">First Invoice Due</div><div class="value" style="color:#dc2626">${fmtINR(Number(client.tariff_amount)+Number(client.previous_balance||0))}</div></div>
      </div>

      <div class="section-title">Payment Details</div>
      <div class="payment-section">
        <div style="display:flex;flex-direction:column;gap:8px;">
          <div class="pay-card">
            <div class="pay-card-title">Bank Transfer</div>
            <div class="pay-row"><span>Account Name</span><span class="val">S Kailash</span></div>
            <div class="pay-row"><span>Bank</span><span class="val">Axis Bank</span></div>
            <div class="pay-row"><span>Account No.</span><span class="val">923010051426791</span></div>
            <div class="pay-row"><span>IFSC</span><span class="val">UTIB0000006</span></div>
          </div>
          <div class="pay-card">
            <div class="pay-card-title">UPI / GPay / PhonePe</div>
            <div class="pay-row"><span>Number</span><span class="val">9943661843</span></div>
          </div>
        </div>
        <div class="qr-box">
          <img src="/payment-qr.svg" alt="QR" onerror="this.parentElement.innerHTML='<div style=width:75px;height:75px;background:#f5f3ff;border-radius:8px;margin:0 auto 5px;display:flex;align-items:center;justify-content:center;font-size:9px;color:#8b7fb0>QR Code</div>'"/>
          <div class="qr-label">Scan to Pay</div>
        </div>
      </div>

      <div class="note-box">
        <p>📌 Invoices will be sent to you before each billing cycle. Kindly ensure timely payment to avoid any service interruptions. For any queries, reach us at 9443394439 or reach360d@gmail.com</p>
      </div>

      <div style="text-align:center;padding:10px 0;">
        <div style="width:130px;border-top:1px solid #1a1035;margin:0 auto 5px;"></div>
        <div style="font-size:12px;font-weight:600;color:#1a1035;">Kailash</div>
        <div style="font-size:10px;color:#6b7280;">Founder, Reach 360</div>
      </div>
    </div>

    <div class="footer">
      <div class="footer-item">📞 9443394439</div>
      <div class="footer-item">✉ reach360d@gmail.com</div>
      <div class="footer-item">🌐 www.reach360.in</div>
    </div>
    <div class="sys-note" style="background:#1a1035;padding-bottom:10px;">This is a system-generated onboarding document.</div>
  </div>
  </body></html>`);
  w.document.close();
  setTimeout(()=>w.print(),600);
};

/* ─── BILLING CYCLE PROGRESS BAR ────────────────────────────── */
const CycleProgress = ({cycle}) => {
  if (!cycle) return null;
  const start = new Date(cycle.cycle_start);
  const end = new Date(cycle.cycle_end);
  const now = new Date();
  const total = (end - start) / (1000*60*60*24);
  const elapsed = Math.min(total, Math.max(0, (now - start) / (1000*60*60*24)));
  const pct = Math.round((elapsed / total) * 100);
  const daysLeft = Math.max(0, Math.ceil((end - now) / (1000*60*60*24)));
  const isOverdue = now > end && Number(cycle.balance_remaining) > 0;
  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',marginBottom:4,fontSize:10,color:T.textSm}}>
        <span>{fmtDate(cycle.cycle_start)}</span>
        <span style={{fontWeight:600,color:isOverdue?T.red:T.textMd}}>{isOverdue?'Overdue':`${daysLeft}d left`}</span>
        <span>{fmtDate(cycle.cycle_end)}</span>
      </div>
      <div style={{height:5,background:'#f0edfb',borderRadius:10,overflow:'hidden'}}>
        <div style={{height:'100%',width:`${pct}%`,background:isOverdue?T.red:'linear-gradient(90deg,#7c3aed,#8b5cf6)',borderRadius:10,transition:'width 0.6s'}}/>
      </div>
    </div>
  );
};

/* ─── CLIENT ROW ────────────────────────────────────────────── */
const ClientRow = ({client, onView, onInvoice, onReceipt, onEdit, onDelete, isAdmin}) => {
  const cycle = client.current_cycle;
  const outstanding = Number(client.total_outstanding||0);
  const totalBilled = Number(client.total_billed||0);
  const totalPaid = Number(client.total_paid||0);

  

  let payStatus = 'unpaid';
  if (totalBilled > 0) {
    if (outstanding === 0) payStatus = 'paid';
    else if (totalPaid > 0) payStatus = 'partial';
    else payStatus = 'unpaid';
  }
  if (cycle?.status === 'overdue') payStatus = 'overdue';

  const borderColor = {paid:T.green,partial:T.yellow,unpaid:T.redBg,overdue:T.orange}[payStatus]||T.border;

  return (
    <div style={{background:T.surface,border:`1px solid ${T.border}`,borderLeft:`3px solid ${borderColor}`,borderRadius:12,padding:'16px 18px',marginBottom:10,boxShadow:T.shadow}}>
      <div style={{display:'flex',alignItems:'flex-start',gap:14}}>
        {/* Avatar */}
        <div style={{width:42,height:42,borderRadius:11,background:T.purpleBg,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:15,color:T.purpleText,flexShrink:0}}>
          {client.company_name?.[0]?.toUpperCase()}
        </div>
        {/* Main info */}
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4,flexWrap:'wrap'}}>
            <span style={{fontWeight:600,fontSize:15,color:T.text}}>{client.company_name}</span>
            <span style={{fontWeight:400,fontSize:11,color:T.textXs,background:'#f5f3ff',padding:'1px 7px',borderRadius:10}}>{client.client_code}</span>
            <StatusBadge status={payStatus}/>
            {client.health_score<70&&<span style={{background:'#fee2e2',color:T.red,fontSize:10,fontWeight:600,padding:'1px 7px',borderRadius:10}}>⚠ Low Health</span>}
          </div>
          <div style={{display:'flex',gap:14,flexWrap:'wrap',marginBottom:8}}>
            <span style={{fontSize:12,color:T.textSm}}><b style={{color:T.textMd}}>{client.contact_person}</b></span>
            <span style={{fontSize:12,color:T.textSm}}>📦 {client.package_name}</span>
            {client.phone&&<span style={{fontSize:12,color:T.textSm}}>📞 {client.phone}</span>}
          </div>
          {/* Cycle progress */}
          {cycle&&<div style={{marginBottom:8}}><CycleProgress cycle={cycle}/></div>}
          {/* Financials */}
          <div style={{display:'flex',gap:16,flexWrap:'wrap'}}>
            <div><span style={{fontSize:11,color:T.textXs}}>Billed</span><div style={{fontWeight:600,fontSize:13,color:T.text}}>{fmtINR(totalBilled)}</div></div>
            <div><span style={{fontSize:11,color:T.textXs}}>Paid</span><div style={{fontWeight:600,fontSize:13,color:T.green}}>{fmtINR(totalPaid)}</div></div>
            <div><span style={{fontSize:11,color:T.textXs}}>Outstanding</span><div style={{fontWeight:600,fontSize:13,color:outstanding>0?T.red:T.green}}>{fmtINR(outstanding)}</div></div>
            <div><span style={{fontSize:11,color:T.textXs}}>Cycle</span><div style={{fontWeight:500,fontSize:12,color:T.textMd}}>{client.billing_cycle_days}d</div></div>
          </div>
        </div>
        {/* Action buttons */}
        <div style={{display:'flex',flexDirection:'column',gap:6,flexShrink:0}}>
          <Btn onClick={()=>onView(client)} variant="secondary" small><Eye size={12}/>View</Btn>
          <Btn onClick={()=>onInvoice(client)} small><FileText size={12}/>Invoice</Btn>
          <Btn onClick={()=>onReceipt(client)} variant="green" small><Receipt size={12}/>Receipt</Btn>
          {isAdmin && (
            <div style={{display:'flex',gap:4}}>
            <button onClick={()=>onEdit(client)} style={{background:T.purpleBg,border:`1px solid #c4b5fd`,color:T.purpleText,cursor:'pointer',borderRadius:7,width:28,height:28,display:'flex',alignItems:'center',justifyContent:'center'}} title="Edit Client"><Edit2 size={12}/></button>
            <button onClick={()=>onDelete(client)} style={{background:'#fee2e2',border:'1px solid #fca5a5',color:T.red,cursor:'pointer',borderRadius:7,width:28,height:28,display:'flex',alignItems:'center',justifyContent:'center'}} title="Remove Client"><Trash2 size={12}/></button>
          </div>
           )}
          <div style={{display:'flex',gap:4}}>
            {(client.whatsapp||client.phone)&&<button onClick={()=>sendWhatsApp(client,'General',client.client_code,0)} style={{background:'#25d366',border:'none',color:'#fff',cursor:'pointer',borderRadius:7,width:28,height:28,display:'flex',alignItems:'center',justifyContent:'center'}} title="WhatsApp"><MessageCircle size={13}/></button>}
            {client.email&&<button onClick={()=>sendEmail(client,'General',client.client_code,0)} style={{background:'#2563eb',border:'none',color:'#fff',cursor:'pointer',borderRadius:7,width:28,height:28,display:'flex',alignItems:'center',justifyContent:'center'}} title="Email"><Mail size={13}/></button>}
          </div>
        </div>
      </div>
    </div>
  );
};


/* ─── EDIT CLIENT MODAL ──────────────────────────────────────── */
function EditClientModal({client, onClose, onSaved}) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [editUsers, setEditUsers] = useState([]);
  useEffect(()=>{ api.get('/auth/users').then(r=>setEditUsers((r.data||[]).filter(u=>u.role!=='admin'))).catch(()=>{}); },[]);
  const editSMMs = editUsers.filter(u=>u.role==='smm');
  const editEditors = editUsers.filter(u=>u.role==='editor');

  const [form, setForm] = useState({
    company_name: client.company_name||'',
    contact_person: client.contact_person||'',
    phone: client.phone||'',
    email: client.email||'',
    whatsapp: client.whatsapp||'',
    address: client.address||'',
    city: client.city||'',
    gst_number: client.gst_number||'',
    assigned_smm: client.assigned_smm||'',
    assigned_editor: client.assigned_editor||'',
    package_name: client.package_name||'',
    services: client.services?.length ? client.services : [{title:''}],
    billing_cycle_days: client.billing_cycle_days||30,
    tariff_amount: client.tariff_amount||'',
    notes: client.notes||'',
  });

  const set = (k,v) => setForm(p=>({...p,[k]:v}));
  const addService = () => setForm(p=>({...p,services:[...p.services,{title:''}]}));
  const removeService = (i) => setForm(p=>({...p,services:p.services.filter((_,idx)=>idx!==i)}));
  const updateService = (i,v) => setForm(p=>({...p,services:p.services.map((s,idx)=>idx===i?(typeof s==='string'?{title:v}:{...s,title:v}):s)}));

  const submit = async () => {
    if (!form.company_name||!form.contact_person||!form.package_name)
      return toast.error('Company name, contact person and package name required');
    setSaving(true);
    try {
      await api.put(`/clients/${client.id}`, {...form, tariff_amount: Number(form.tariff_amount)});
      toast.success('Client updated successfully');
      onSaved();
    } catch(e) { toast.error(e.response?.data?.error||'Failed to update'); }
    finally { setSaving(false); }
  };

  const steps = ['Company Info','Package & Services','Billing Setup'];

  return (
    <Modal title="Edit Client" subtitle={`${client.company_name} · ${client.client_code}`} onClose={onClose} wide>
      {/* Step indicator */}
      <div style={{display:'flex',gap:0,marginBottom:22,background:'#f5f3ff',borderRadius:10,padding:4}}>
        {steps.map((s,i)=>(
          <button key={i} onClick={()=>setStep(i+1)}
            style={{flex:1,background:step===i+1?T.purple:'transparent',color:step===i+1?'#fff':T.textSm,border:'none',borderRadius:8,padding:'7px',fontSize:12,fontWeight:step===i+1?600:400,cursor:'pointer',fontFamily:T.font,transition:'all 0.2s'}}>
            {i+1}. {s}
          </button>
        ))}
      </div>

      {step===1&&(
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
          {[['company_name','Company Name *'],['contact_person','Contact Person *'],['phone','Phone Number'],['email','Email'],['whatsapp','WhatsApp Number'],['gst_number','GST Number']].map(([k,l])=>(
            <div key={k}>
              <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>{l}</label>
              <input style={inputSt} value={form[k]} onChange={e=>set(k,e.target.value)} placeholder={l.replace(' *','')}/>
            </div>
          ))}
          <div style={{gridColumn:'1/-1'}}>
            <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Address</label>
            <input style={inputSt} value={form.address} onChange={e=>set('address',e.target.value)}/>
          </div>
          <div>
            <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>City</label>
            <input style={inputSt} value={form.city} onChange={e=>set('city',e.target.value)}/>
          </div>
          <div>
            <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Assigned SMM</label>
            <select style={inputSt} value={form.assigned_smm} onChange={e=>set('assigned_smm',e.target.value)}>
              <option value="">Select SMM...</option>
              {editSMMs.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Assigned Editor</label>
            <select style={inputSt} value={form.assigned_editor} onChange={e=>set('assigned_editor',e.target.value)}>
              <option value="">Select Editor...</option>
              {editEditors.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
        </div>
      )}

      {step===2&&(
        <div>
          <div style={{marginBottom:16}}>
            <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Package Name *</label>
            <input style={inputSt} value={form.package_name} onChange={e=>set('package_name',e.target.value)} placeholder="Package / Tier name"/>
          </div>
          <div>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
              <label style={{fontWeight:600,fontSize:12,color:T.textMd}}>Services / Deliverables</label>
              <Btn onClick={addService} variant="secondary" small><Plus size={11}/>Add Line</Btn>
            </div>
            {form.services.map((s,i)=>(
              <div key={i} style={{display:'flex',gap:8,marginBottom:8}}>
                <div style={{width:26,height:26,background:T.purple,borderRadius:'50%',color:'#fff',fontSize:10,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,marginTop:8}}>{i+1}</div>
                <input style={{...inputSt,flex:1}} value={typeof s === 'string' ? s : (s.title||'')} onChange={e=>updateService(i,e.target.value)} placeholder={`Service ${i+1}`}/>
                {form.services.length>1&&<button onClick={()=>removeService(i)} style={{background:'#fee2e2',border:'none',color:T.red,cursor:'pointer',borderRadius:8,width:34,height:34,display:'flex',alignItems:'center',justifyContent:'center',marginTop:4,flexShrink:0}}><X size={13}/></button>}
              </div>
            ))}
          </div>
        </div>
      )}

      {step===3&&(
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
          <div>
            <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Billing Cycle (days)</label>
            <select style={inputSt} value={form.billing_cycle_days} onChange={e=>set('billing_cycle_days',Number(e.target.value))}>
              {[{v:30,l:'30 Days (Monthly)'},{v:45,l:'45 Days'},{v:60,l:'60 Days (Bi-Monthly)'},{v:90,l:'90 Days (Quarterly)'}].map(o=><option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </div>
          <div>
            <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Tariff Amount (₹)</label>
            <input type="number" style={inputSt} value={form.tariff_amount} onChange={e=>set('tariff_amount',e.target.value)}/>
          </div>
          <div style={{gridColumn:'1/-1'}}>
            <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Notes</label>
            <textarea style={{...inputSt,resize:'vertical',lineHeight:1.6}} rows={2} value={form.notes} onChange={e=>set('notes',e.target.value)}/>
          </div>
          <div style={{gridColumn:'1/-1',background:'#fef3c7',border:'1px solid #fcd34d',borderRadius:10,padding:'10px 14px'}}>
            <p style={{fontSize:12,color:'#92400e',fontWeight:500}}>⚠ Changing tariff amount here updates the client profile only. It applies to the next new invoice generated — existing invoices are not changed.</p>
          </div>
        </div>
      )}

      <div style={{display:'flex',justifyContent:'space-between',marginTop:20}}>
        <Btn variant="secondary" onClick={()=>step>1?setStep(s=>s-1):onClose()}>{step>1?'← Back':'Cancel'}</Btn>
        {step<3?<Btn onClick={()=>setStep(s=>s+1)}>Next →</Btn>:<Btn onClick={submit} disabled={saving}>{saving?'Saving...':'Save Changes'}</Btn>}
      </div>
    </Modal>
  );
}

/* ─── ADD CLIENT FORM ─────────────────────────────────────────── */
function AddClientModal({onClose, onSaved}) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  useEffect(()=>{ api.get('/auth/users').then(r=>setUsers((r.data||[]).filter(u=>u.role!=='admin'))).catch(()=>{}); },[]);
  const smms = users.filter(u=>u.role==='smm');
  const editors = users.filter(u=>u.role==='editor');

  const [form, setForm] = useState({
    company_name:'', contact_person:'', phone:'', email:'', whatsapp:'',
    address:'', city:'', gst_number:'',
    assigned_smm:'', assigned_editor:'',
    package_name:'', services:[{title:''}],
    billing_cycle_days:30, billing_start_date:'', tariff_amount:'',
    previous_balance:0, notes:''
  });

  const set = (k,v) => setForm(p=>({...p,[k]:v}));
  const addService = () => setForm(p=>({...p,services:[...p.services,{title:''}]}));
  const removeService = (i) => setForm(p=>({...p,services:p.services.filter((_,idx)=>idx!==i)}));
  const updateService = (i,v) => setForm(p=>({...p,services:p.services.map((s,idx)=>idx===i?{...s,title:v}:s)}));

  const submit = async () => {
    if (!form.company_name||!form.contact_person||!form.package_name||!form.billing_start_date||!form.tariff_amount)
      return toast.error('Fill all required fields');
    setSaving(true);
    try {
      const res = await api.post('/clients', { ...form, tariff_amount: Number(form.tariff_amount), previous_balance: Number(form.previous_balance||0) });
      toast.success(`Client ${form.company_name} added!`);
      // Auto-generate onboarding PDF
      setTimeout(()=>{
        generateOnboardingPDF({...res.data, ...form, tariff_amount: Number(form.tariff_amount)});
      }, 400);
      onSaved();
    } catch(e) { toast.error(e.response?.data?.error||'Failed'); }
    finally { setSaving(false); }
  };

  const steps = ['Company Info','Package & Services','Billing Setup'];

  return (
    <Modal title="Add New Client" subtitle="Fill all sections to onboard client" onClose={onClose} wide>
      {/* Step indicator */}
      <div style={{display:'flex',gap:0,marginBottom:22,background:'#f5f3ff',borderRadius:10,padding:4}}>
        {steps.map((s,i)=>(
          <button key={i} onClick={()=>setStep(i+1)}
            style={{flex:1,background:step===i+1?T.purple:'transparent',color:step===i+1?'#fff':T.textSm,border:'none',borderRadius:8,padding:'7px',fontSize:12,fontWeight:step===i+1?600:400,cursor:'pointer',fontFamily:T.font,transition:'all 0.2s'}}>
            {i+1}. {s}
          </button>
        ))}
      </div>

      {step===1&&(
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
          {[['company_name','Company Name *'],['contact_person','Contact Person *'],['phone','Phone Number'],['email','Email'],['whatsapp','WhatsApp Number'],['gst_number','GST Number']].map(([k,l])=>(
            <div key={k}>
              <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>{l}</label>
              <input style={inputSt} value={form[k]} onChange={e=>set(k,e.target.value)} placeholder={l.replace(' *','')}/>
            </div>
          ))}
          <div style={{gridColumn:'1/-1'}}>
            <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Address</label>
            <input style={inputSt} value={form.address} onChange={e=>set('address',e.target.value)} placeholder="Full address"/>
          </div>
          <div>
            <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>City</label>
            <input style={inputSt} value={form.city} onChange={e=>set('city',e.target.value)} placeholder="City"/>
          </div>
          <div>
            <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Assigned SMM</label>
            <select style={inputSt} value={form.assigned_smm} onChange={e=>set('assigned_smm',e.target.value)}>
              <option value="">Select SMM...</option>
              {smms.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Assigned Editor</label>
            <select style={inputSt} value={form.assigned_editor} onChange={e=>set('assigned_editor',e.target.value)}>
              <option value="">Select Editor...</option>
              {editors.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
        </div>
      )}

      {step===2&&(
        <div>
          <div style={{marginBottom:16}}>
            <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Package Name * <span style={{fontWeight:400,color:T.textXs}}>(e.g. Tiers Custom, Basic Plan)</span></label>
            <input style={inputSt} value={form.package_name} onChange={e=>set('package_name',e.target.value)} placeholder="Package / Tier name"/>
          </div>
          <div style={{marginBottom:8}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
              <label style={{fontWeight:600,fontSize:12,color:T.textMd}}>Services / Deliverables (add line by line)</label>
              <Btn onClick={addService} variant="secondary" small><Plus size={11}/>Add Line</Btn>
            </div>
            {form.services.map((s,i)=>(
              <div key={i} style={{display:'flex',gap:8,marginBottom:8}}>
                <div style={{width:26,height:26,background:T.purple,borderRadius:'50%',color:'#fff',fontSize:10,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,marginTop:8}}>{i+1}</div>
                <input style={{...inputSt,flex:1}} value={typeof s === 'string' ? s : (s.title||'')} onChange={e=>updateService(i,e.target.value)} placeholder={`Service ${i+1} description`}/>
                {form.services.length>1&&<button onClick={()=>removeService(i)} style={{background:'#fee2e2',border:'none',color:T.red,cursor:'pointer',borderRadius:8,width:34,height:34,display:'flex',alignItems:'center',justifyContent:'center',marginTop:4,flexShrink:0}}><X size={13}/></button>}
              </div>
            ))}
          </div>
        </div>
      )}

      {step===3&&(
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
          <div>
            <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Billing Cycle (days) *</label>
            <select style={inputSt} value={form.billing_cycle_days} onChange={e=>set('billing_cycle_days',Number(e.target.value))}>
              {[{v:30,l:'30 Days (Monthly)'},{v:45,l:'45 Days'},{v:60,l:'60 Days (Bi-Monthly)'},{v:90,l:'90 Days (Quarterly)'}].map(o=><option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </div>
          <div>
            <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Billing Start Date *</label>
            <input type="date" style={inputSt} value={form.billing_start_date} onChange={e=>set('billing_start_date',e.target.value)}/>
          </div>
          <div>
            <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Tariff Amount (₹) *</label>
            <input type="number" style={inputSt} value={form.tariff_amount} onChange={e=>set('tariff_amount',e.target.value)} placeholder="e.g. 35000"/>
          </div>
          <div>
            <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Previous Balance (₹)</label>
            <input type="number" style={inputSt} value={form.previous_balance} onChange={e=>set('previous_balance',e.target.value)} placeholder="0"/>
          </div>
          <div style={{gridColumn:'1/-1'}}>
            <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Notes</label>
            <textarea style={{...inputSt,resize:'vertical',lineHeight:1.6}} rows={2} value={form.notes} onChange={e=>set('notes',e.target.value)} placeholder="Any additional notes..."/>
          </div>
          {form.tariff_amount&&(
            <div style={{gridColumn:'1/-1',background:T.purpleBg,border:`1px solid #c4b5fd`,borderRadius:10,padding:'12px 14px'}}>
              <div style={{fontWeight:600,fontSize:12,color:T.purpleText,marginBottom:6}}>Billing Summary Preview</div>
              <div style={{fontSize:12,color:T.textMd}}>Package: <b>{form.package_name}</b> · Tariff: <b>{fmtINR(Number(form.tariff_amount))}</b> · Cycle: <b>{form.billing_cycle_days} days</b></div>
              {Number(form.previous_balance)>0&&<div style={{fontSize:12,color:T.yellow,marginTop:3}}>Previous Balance: {fmtINR(Number(form.previous_balance))} → Total First Due: {fmtINR(Number(form.tariff_amount)+Number(form.previous_balance))}</div>}
            </div>
          )}
        </div>
      )}

      <div style={{display:'flex',justifyContent:'space-between',marginTop:20}}>
        <Btn variant="secondary" onClick={()=>step>1?setStep(s=>s-1):onClose()}>{step>1?'← Back':'Cancel'}</Btn>
        {step<3?<Btn onClick={()=>setStep(s=>s+1)}>Next →</Btn>:<Btn onClick={submit} disabled={saving}>{saving?'Saving...':'Save Client'}</Btn>}
      </div>
    </Modal>
  );
}

/* ─── GENERATE INVOICE MODAL ────────────────────────────────── */
function InvoiceModal({client, onClose, onSaved}) {
  const [cycles, setCycles] = useState([]);
  const [form, setForm] = useState({ cycle_id:'', invoice_date:new Date().toISOString().split('T')[0], due_date:'', billing_period_start:'', billing_period_end:'', previous_balance:client.previous_balance||0, notes:'' });
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(null);

  useEffect(()=>{
    api.get(`/clients/${client.id}/cycles`).then(r=>{
      setCycles(r.data);
      const active=(r.data||[]).find(c=>c.status==='active')||r.data?.[0];
      if(active){
        setForm(f=>({...f,
          billing_period_start: active.cycle_start||'',
          billing_period_end:   active.cycle_end||'',
          due_date: f.due_date||active.cycle_end||'',
        }));
      }
    }).catch(()=>{});
  },[client.id]);

  const submit = async () => {
    setLoading(true);
    try {
      const r = await api.post('/invoices',{ client_id:client.id, ...form });
      setGenerated(r.data);
      toast.success(`Invoice ${r.data.invoice_number} generated!`);
      onSaved();
    } catch(e){ toast.error(e.response?.data?.error||'Failed'); }
    finally{ setLoading(false); }
  };

  if (generated) return (
    <Modal title="Invoice Generated!" subtitle={generated.invoice_number} onClose={onClose}
      actions={[
        <Btn key="dl" onClick={async()=>{ const r=await api.get(`/invoices/${generated.id}/print`); generateInvoicePDF(r.data); }} ><Download size={13}/>Download PDF</Btn>,
        <Btn key="wa" variant="wa" onClick={()=>sendWhatsApp(client,'Invoice',generated.invoice_number,generated.total_amount)}><MessageCircle size={13}/>WhatsApp</Btn>,
        <Btn key="em" variant="mail" onClick={()=>sendEmail(client,'Invoice',generated.invoice_number,generated.total_amount)}><Mail size={13}/>Email</Btn>,
        <Btn key="c" variant="secondary" onClick={onClose}>Close</Btn>,
      ]}>
      <div style={{background:T.greenBg,border:`1px solid ${T.greenBorder}`,borderRadius:10,padding:'14px 16px',marginBottom:10}}>
        <div style={{fontWeight:600,fontSize:14,color:T.green,marginBottom:6}}>{generated.invoice_number} · {generated.client_invoice_number}</div>
        <div style={{fontSize:13,color:T.textMd}}>Total: <b>{fmtINR(generated.total_amount)}</b> · Package: {generated.package_name}</div>
      </div>
      <p style={{fontSize:12,color:T.textSm}}>Download the PDF first, then send via WhatsApp or Email.</p>
    </Modal>
  );

  const activeCycle = cycles[0];
  const total = Number(client.tariff_amount) + Number(form.previous_balance||0);

  return (
    <Modal title={`Generate Invoice`} subtitle={`${client.company_name} · ${client.client_code}`} onClose={onClose}
      actions={[<Btn key="c" variant="secondary" onClick={onClose}>Cancel</Btn>,<Btn key="s" onClick={submit} disabled={loading}>{loading?'Generating...':'Generate Invoice'}</Btn>]}>
      {activeCycle&&(
        <div style={{background:T.purpleBg,border:`1px solid #c4b5fd`,borderRadius:10,padding:'12px 14px',marginBottom:14}}>
          <div style={{fontSize:12,color:T.purpleText,fontWeight:600}}>Current Cycle: {fmtDate(activeCycle.cycle_start)} → {fmtDate(activeCycle.cycle_end)}</div>
          <div style={{fontSize:12,color:T.textMd,marginTop:3}}>Total Due: {fmtINR(activeCycle.total_due)} · Paid: {fmtINR(activeCycle.total_paid)} · Balance: {fmtINR(activeCycle.balance_remaining)}</div>
        </div>
      )}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
        {[['invoice_date','Invoice Date','date'],['due_date','Due Date','date']].map(([k,l,t])=>(
          <div key={k}>
            <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>{l}</label>
            <input type={t} style={inputSt} value={form[k]} onChange={e=>setForm(p=>({...p,[k]:e.target.value}))}/>
          </div>
        ))}
      </div>
      <div style={{marginTop:12,marginBottom:12}}>
        <label style={{display:'block',fontWeight:700,fontSize:12,color:T.textMd,marginBottom:6}}>Billing Period <span style={{fontWeight:400,color:T.textXs}}>(edit if wrong)</span></label>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          <div>
            <label style={{display:'block',fontWeight:500,fontSize:11,color:T.textSm,marginBottom:4}}>From</label>
            <input type="date" style={inputSt} value={form.billing_period_start} onChange={e=>setForm(p=>({...p,billing_period_start:e.target.value}))}/>
          </div>
          <div>
            <label style={{display:'block',fontWeight:500,fontSize:11,color:T.textSm,marginBottom:4}}>To</label>
            <input type="date" style={inputSt} value={form.billing_period_end} onChange={e=>setForm(p=>({...p,billing_period_end:e.target.value}))}/>
          </div>
        </div>
        {form.billing_period_start&&form.billing_period_end&&<div style={{marginTop:5,fontSize:11,color:T.purpleText,background:T.purpleBg,borderRadius:7,padding:'4px 10px',display:'inline-block'}}>📅 {fmtDate(form.billing_period_start)} → {fmtDate(form.billing_period_end)}</div>}
      </div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
        <div>
          <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Previous Balance (₹)</label>
          <input type="number" style={inputSt} value={form.previous_balance} onChange={e=>setForm(p=>({...p,previous_balance:e.target.value}))}/>
        </div>
        <div style={{display:'flex',flexDirection:'column',justifyContent:'flex-end'}}>
          <div style={{background:'#f5f3ff',borderRadius:9,padding:'10px 12px',fontSize:13}}>
            Total: <b style={{color:T.purple}}>{fmtINR(total)}</b>
          </div>
        </div>
      </div>
      <div style={{marginTop:12}}>
        <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Notes</label>
        <textarea style={{...inputSt,resize:'vertical',lineHeight:1.6}} rows={2} value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))}/>
      </div>
    </Modal>
  );
}

/* ─── RECORD PAYMENT MODAL ──────────────────────────────────── */
function ReceiptModal({client, onClose, onSaved}) {
  const [invoices, setInvoices] = useState([]);
  const [form, setForm] = useState({ invoice_id:'', receipt_date:new Date().toISOString().split('T')[0], amount_received:'', payment_mode:'cash', transaction_ref:'', notes:'' });
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(null);

  useEffect(()=>{
    api.get('/invoices',{params:{client_id:client.id}}).then(r=>setInvoices(r.data.filter(i=>i.status!=='paid'&&i.status!=='cancelled'))).catch(()=>{});
  },[client.id]);

  const submit = async () => {
    if (!form.amount_received) return toast.error('Enter amount received');
    setLoading(true);
    try {
      const r = await api.post('/invoices/receipts',{ client_id:client.id, ...form, amount_received:Number(form.amount_received) });
      setGenerated(r.data);
      toast.success(`Receipt ${r.data.receipt_number} generated!`);
      onSaved();
    } catch(e){ toast.error(e.response?.data?.error||'Failed'); }
    finally{ setLoading(false); }
  };

  if (generated) return (
    <Modal title="Payment Recorded!" subtitle={generated.receipt_number} onClose={onClose}
      actions={[
        <Btn key="dl" onClick={async()=>{ const r=await api.get(`/invoices/receipts/${generated.id}/print`); generateReceiptPDF(r.data); }}><Download size={13}/>Download PDF</Btn>,
        <Btn key="wa" variant="wa" onClick={()=>sendWhatsApp(client,'Receipt',generated.receipt_number,generated.amount_received)}><MessageCircle size={13}/>WhatsApp</Btn>,
        <Btn key="em" variant="mail" onClick={()=>sendEmail(client,'Receipt',generated.receipt_number,generated.amount_received)}><Mail size={13}/>Email</Btn>,
        <Btn key="c" variant="secondary" onClick={onClose}>Close</Btn>,
      ]}>
      <div style={{background:T.greenBg,border:`1px solid ${T.greenBorder}`,borderRadius:10,padding:'14px 16px',marginBottom:10}}>
        <div style={{fontWeight:600,fontSize:14,color:T.green}}>{generated.receipt_number}</div>
        <div style={{fontSize:13,color:T.textMd,marginTop:4}}>Received: <b>{fmtINR(generated.amount_received)}</b> · Balance: <b style={{color:Number(generated.balance_remaining)>0?T.red:T.green}}>{fmtINR(generated.balance_remaining)}</b></div>
      </div>
      <p style={{fontSize:12,color:T.textSm}}>Download the PDF first, then send via WhatsApp or Email.</p>
    </Modal>
  );

  return (
    <Modal title="Record Payment" subtitle={`${client.company_name} · ${client.client_code}`} onClose={onClose}
      actions={[<Btn key="c" variant="secondary" onClick={onClose}>Cancel</Btn>,<Btn key="s" variant="green" onClick={submit} disabled={loading}>{loading?'Saving...':'Record Payment'}</Btn>]}>
      {invoices.length>0&&(
        <div style={{marginBottom:14}}>
          <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Link to Invoice (optional)</label>
          <select style={inputSt} value={form.invoice_id} onChange={e=>setForm(p=>({...p,invoice_id:e.target.value}))}>
            <option value="">No specific invoice</option>
            {invoices.map(i=><option key={i.id} value={i.id}>{i.invoice_number} — {fmtINR(i.total_amount)} ({i.status})</option>)}
          </select>
        </div>
      )}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
        <div>
          <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Receipt Date</label>
          <input type="date" style={inputSt} value={form.receipt_date} onChange={e=>setForm(p=>({...p,receipt_date:e.target.value}))}/>
        </div>
        <div>
          <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Amount Received (₹) *</label>
          <input type="number" style={inputSt} value={form.amount_received} onChange={e=>setForm(p=>({...p,amount_received:e.target.value}))} placeholder="e.g. 17500"/>
        </div>
        <div>
          <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Payment Mode</label>
          <select style={inputSt} value={form.payment_mode} onChange={e=>setForm(p=>({...p,payment_mode:e.target.value}))}>
            {[['cash','Cash'],['gpay','GPay'],['phonepe','PhonePe'],['upi','UPI'],['bank_transfer','Bank Transfer'],['cheque','Cheque'],['other','Other']].map(([v,l])=><option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Transaction Ref</label>
          <input style={inputSt} value={form.transaction_ref} onChange={e=>setForm(p=>({...p,transaction_ref:e.target.value}))} placeholder="UTR / Transaction ID"/>
        </div>
      </div>
      <div>
        <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Notes</label>
        <textarea style={{...inputSt,resize:'vertical',lineHeight:1.6}} rows={2} value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))}/>
      </div>
    </Modal>
  );
}

/* ─── DELETE CONFIRMATION MODAL ──────────────────────────────── */
function DeleteClientModal({client, onClose, onDeleted}) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const del = async () => {
    if (!reason.trim()) return toast.error('Please provide a reason');
    setLoading(true);
    try {
      await api.delete(`/clients/${client.id}`, { data: { reason } });
      toast.success(`${client.company_name} removed`);
      onDeleted();
    } catch(e) { toast.error(e.response?.data?.error||'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <Modal title="Remove Client" subtitle={`${client.company_name} · ${client.client_code}`} onClose={onClose}>
      <div style={{background:'#fff7ed',border:'1px solid #fed7aa',borderRadius:10,padding:'12px 14px',marginBottom:14}}>
        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
          <AlertTriangle size={15} color={T.orange}/>
          <span style={{fontWeight:600,fontSize:13,color:T.orange}}>This client will be moved to Previous Clients</span>
        </div>
        <p style={{fontSize:12,color:'#9a3412'}}>All invoices, receipts and billing history are preserved. You can restore this client anytime from the Previous Clients tab.</p>
      </div>
      <div style={{marginBottom:14}}>
        <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Reason for removal *</label>
        <textarea style={{...inputSt,resize:'vertical',lineHeight:1.6}} rows={3} value={reason} onChange={e=>setReason(e.target.value)} placeholder="e.g. Contract ended, Client requested removal..."/>
      </div>
      <div style={{display:'flex',justifyContent:'flex-end',gap:9}}>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
        <Btn variant="red" onClick={del} disabled={loading}>{loading?'Removing...':'Remove Client'}</Btn>
      </div>
    </Modal>
  );
}

/* ─── EDIT INVOICE MODAL ────────────────────────────────────── */
function EditInvoiceModal({ invoice, client, onClose, onSaved }) {
  const [form, setForm] = useState({
    tariff_amount:    String(invoice.tariff_amount || invoice.total_amount || ''),
    previous_balance: String(invoice.previous_balance || 0),
    due_date:         invoice.due_date || '',
    notes:            invoice.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const newTotal = Number(form.tariff_amount || 0) + Number(form.previous_balance || 0);

  const submit = async () => {
    if (!form.tariff_amount || isNaN(Number(form.tariff_amount))) {
      return toast.error('Enter a valid tariff amount');
    }
    setSaving(true);
    try {
      await api.put(`/invoices/${invoice.id}/edit-amount`, {
        tariff_amount:    Number(form.tariff_amount),
        previous_balance: Number(form.previous_balance || 0),
        due_date:         form.due_date || null,
        notes:            form.notes,
      });
      toast.success('Invoice updated successfully');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to update invoice');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Edit Invoice Amount"
      subtitle={`${invoice.invoice_number} · ${client.company_name}`}
      onClose={onClose}
      actions={[
        <Btn key="c" variant="secondary" onClick={onClose}>Cancel</Btn>,
        <Btn key="s" onClick={submit} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </Btn>,
      ]}
    >
      <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: '11px 14px', marginBottom: 16 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: '#9a3412' }}>⚠ This edits the already-generated invoice</div>
        <div style={{ fontSize: 12, color: '#92400e', marginTop: 3 }}>
          Current amount: <b>{fmtINR(invoice.total_amount)}</b> → New amount will be: <b style={{ color: '#7c3aed' }}>{fmtINR(newTotal)}</b>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div>
          <label style={{ display: 'block', fontWeight: 600, fontSize: 12, color: T.textMd, marginBottom: 5 }}>
            Tariff Amount (₹) *
          </label>
          <input
            type="number"
            style={inputSt}
            value={form.tariff_amount}
            onChange={e => setForm(p => ({ ...p, tariff_amount: e.target.value }))}
            placeholder="e.g. 22000"
          />
        </div>
        <div>
          <label style={{ display: 'block', fontWeight: 600, fontSize: 12, color: T.textMd, marginBottom: 5 }}>
            Previous Balance (₹)
          </label>
          <input
            type="number"
            style={inputSt}
            value={form.previous_balance}
            onChange={e => setForm(p => ({ ...p, previous_balance: e.target.value }))}
            placeholder="0"
          />
        </div>
        <div>
          <label style={{ display: 'block', fontWeight: 600, fontSize: 12, color: T.textMd, marginBottom: 5 }}>
            Due Date
          </label>
          <input
            type="date"
            style={inputSt}
            value={form.due_date}
            onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
          <div style={{ background: T.purpleBg, border: `1px solid #c4b5fd`, borderRadius: 9, padding: '10px 12px', fontSize: 13 }}>
            New Total: <b style={{ color: T.purple }}>{fmtINR(newTotal)}</b>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <label style={{ display: 'block', fontWeight: 600, fontSize: 12, color: T.textMd, marginBottom: 5 }}>Notes</label>
        <textarea
          style={{ ...inputSt, resize: 'vertical', lineHeight: 1.6 }}
          rows={2}
          value={form.notes}
          onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
        />
      </div>
    </Modal>
  );
}

/* ─── EDIT RECEIPT MODAL ─────────────────────────────────────── */
function EditReceiptModal({ receipt, client, onClose, onSaved }) {
  const [form, setForm] = useState({
    amount_received:  String(receipt.amount_received || ''),
    payment_mode:     receipt.payment_mode  || 'cash',
    receipt_date:     receipt.receipt_date  || '',
    transaction_ref:  receipt.transaction_ref || '',
    notes:            receipt.notes         || '',
  });
  const [saving, setSaving] = useState(false);

  const newAmount       = Number(form.amount_received || 0);
  const oldAmount       = Number(receipt.amount_received || 0);
  const amountChanged   = newAmount !== oldAmount;

  const submit = async () => {
    if (!form.amount_received || isNaN(newAmount) || newAmount <= 0) {
      return toast.error('Enter a valid amount');
    }
    setSaving(true);
    try {
      await api.put(`/invoices/receipts/${receipt.id}/edit-amount`, {
        amount_received:  newAmount,
        payment_mode:     form.payment_mode,
        receipt_date:     form.receipt_date || null,
        transaction_ref:  form.transaction_ref,
        notes:            form.notes,
      });
      toast.success('Receipt updated successfully');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to update receipt');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Edit Receipt"
      subtitle={`${receipt.receipt_number} · ${client.company_name}`}
      onClose={onClose}
      actions={[
        <Btn key="c" variant="secondary" onClick={onClose}>Cancel</Btn>,
        <Btn key="s" onClick={submit} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </Btn>,
      ]}
    >
      {/* Warning banner */}
      <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: '11px 14px', marginBottom: 16 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: '#9a3412' }}>⚠ This edits the already-recorded receipt</div>
        <div style={{ fontSize: 12, color: '#92400e', marginTop: 3 }}>
          Current amount: <b>{fmtINR(oldAmount)}</b>
          {amountChanged && (
            <span> → New amount: <b style={{ color: '#7c3aed' }}>{fmtINR(newAmount)}</b></span>
          )}
        </div>
        <div style={{ fontSize: 11, color: '#92400e', marginTop: 4 }}>
          Client totals, invoice status and payment balance will all recalculate automatically.
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        {/* Amount */}
        <div>
          <label style={{ display: 'block', fontWeight: 600, fontSize: 12, color: T.textMd, marginBottom: 5 }}>
            Amount Received (₹) *
          </label>
          <input
            type="number"
            style={inputSt}
            value={form.amount_received}
            onChange={e => setForm(p => ({ ...p, amount_received: e.target.value }))}
            placeholder="e.g. 22000"
          />
        </div>

        {/* Payment mode */}
        <div>
          <label style={{ display: 'block', fontWeight: 600, fontSize: 12, color: T.textMd, marginBottom: 5 }}>
            Payment Mode
          </label>
          <select style={inputSt} value={form.payment_mode} onChange={e => setForm(p => ({ ...p, payment_mode: e.target.value }))}>
            {[['cash','Cash'],['gpay','GPay'],['phonepe','PhonePe'],['upi','UPI'],['bank_transfer','Bank Transfer'],['cheque','Cheque'],['other','Other']]
              .map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>

        {/* Receipt date */}
        <div>
          <label style={{ display: 'block', fontWeight: 600, fontSize: 12, color: T.textMd, marginBottom: 5 }}>
            Receipt Date
          </label>
          <input
            type="date"
            style={inputSt}
            value={form.receipt_date}
            onChange={e => setForm(p => ({ ...p, receipt_date: e.target.value }))}
          />
        </div>

        {/* New balance preview */}
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
          <div style={{
            background: amountChanged ? T.purpleBg : '#f5f3ff',
            border: `1px solid ${amountChanged ? '#c4b5fd' : T.border}`,
            borderRadius: 9, padding: '10px 12px', fontSize: 13
          }}>
            {amountChanged
              ? <><b style={{ color: T.purple }}>{fmtINR(newAmount)}</b> <span style={{ fontSize: 11, color: T.textSm }}>new amount</span></>
              : <span style={{ color: T.textSm }}>Amount unchanged</span>
            }
          </div>
        </div>

        {/* Transaction ref */}
        <div style={{ gridColumn: '1/-1' }}>
          <label style={{ display: 'block', fontWeight: 600, fontSize: 12, color: T.textMd, marginBottom: 5 }}>
            Transaction Ref / UTR
          </label>
          <input
            style={inputSt}
            value={form.transaction_ref}
            onChange={e => setForm(p => ({ ...p, transaction_ref: e.target.value }))}
            placeholder="UTR / Transaction ID"
          />
        </div>

        {/* Notes */}
        <div style={{ gridColumn: '1/-1' }}>
          <label style={{ display: 'block', fontWeight: 600, fontSize: 12, color: T.textMd, marginBottom: 5 }}>
            Notes
          </label>
          <textarea
            style={{ ...inputSt, resize: 'vertical', lineHeight: 1.6 }}
            rows={2}
            value={form.notes}
            onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
          />
        </div>
      </div>
    </Modal>
  );
}

/* ─── CLIENT DETAIL VIEW ─────────────────────────────────────── */
function ClientDetail({client: initialClient, onBack, onRefresh}) {
  const [client, setClient] = useState(initialClient);
  const [activeTab, setActiveTab] = useState('overview');
  const [billing, setBilling] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [showNewCycle, setShowNewCycle] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null); // ADD THIS
  const [editReceipt, setEditReceipt] = useState(null); 

  useEffect(()=>{
    api.get(`/clients/${client.id}`).then(r=>setClient(r.data)).catch(()=>{});
    api.get(`/invoices/billing/${client.id}`).then(r=>setBilling(r.data)).catch(()=>{});
    api.get(`/clients/${client.id}/activity`).then(r=>setActivity(r.data)).catch(()=>{});
  },[client.id]);

  const tabs = [{id:'overview',l:'Overview'},{id:'invoices',l:`Invoices (${billing?.invoices?.length||0})`},{id:'receipts',l:`Receipts (${billing?.receipts?.length||0})`},{id:'billing',l:'Billing History'},{id:'activity',l:'Activity Log'}];

  return (
    <div>
      {/* Back header */}
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:20}}>
        <button onClick={onBack} style={{background:T.purpleBg,border:`1px solid #c4b5fd`,color:T.purple,cursor:'pointer',borderRadius:9,padding:'8px 14px',fontSize:13,fontWeight:600,fontFamily:T.font,display:'flex',alignItems:'center',gap:6}}>
          ← Back to Clients
        </button>
        <div>
          <div style={{fontWeight:700,fontSize:20,color:T.text}}>{client.company_name}</div>
          <div style={{fontSize:12,color:T.textSm}}>{client.client_code} · {client.package_name}</div>
        </div>
        <div style={{marginLeft:'auto',display:'flex',gap:8}}>
          <Btn variant="secondary" onClick={()=>setShowEdit(true)}><Edit2 size={13}/>Edit</Btn>
          <Btn onClick={()=>setShowInvoice(true)}><FileText size={13}/>New Invoice</Btn>
          <Btn variant="green" onClick={()=>setShowReceipt(true)}><Receipt size={13}/>Record Payment</Btn>
          <button onClick={()=>generateOnboardingPDF(client)} style={{background:'#f5f3ff',border:`1px solid #c4b5fd`,color:T.purpleText,cursor:'pointer',borderRadius:9,padding:'9px 14px',fontSize:13,fontWeight:600,fontFamily:T.font,display:'flex',alignItems:'center',gap:5}} title="Download Onboarding PDF">
            <Download size={13}/>Onboarding PDF
          </button>
        </div>
      </div>

      {/* Sub-tabs */}
      <div style={{display:'flex',gap:0,borderBottom:`2px solid ${T.borderL}`,marginBottom:20}}>
        {tabs.map(t=>(
          <button key={t.id} onClick={()=>setActiveTab(t.id)}
            style={{background:'none',border:'none',padding:'9px 16px',fontSize:13,fontWeight:activeTab===t.id?600:400,color:activeTab===t.id?T.purple:T.textSm,cursor:'pointer',fontFamily:T.font,borderBottom:activeTab===t.id?`2px solid ${T.purple}`:'2px solid transparent',marginBottom:-2}}>
            {t.l}
          </button>
        ))}
      </div>

      {/* OVERVIEW */}
      {activeTab==='overview'&&(
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
          {/* Client Info */}
          <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:14,padding:'18px 20px',boxShadow:T.shadow}}>
            <div style={{fontWeight:700,fontSize:14,color:T.text,marginBottom:14}}>Client Information</div>
            {[['Company',client.company_name],['Contact',client.contact_person],['Phone',client.phone],['Email',client.email],['WhatsApp',client.whatsapp],['Address',client.address],['City',client.city],['GST No.',client.gst_number],['Client Code',client.client_code],['Assigned SMM',client.smm?.name],['Assigned Editor',client.editor?.name]].map(([l,v])=>v?(
              <div key={l} style={{display:'flex',gap:8,padding:'6px 0',borderBottom:`1px solid ${T.borderL}`,fontSize:13}}>
                <span style={{color:T.textSm,fontWeight:500,minWidth:90}}>{l}</span>
                <span style={{color:T.text,fontWeight:400}}>{v}</span>
              </div>
            ):null)}
          </div>
          {/* Financial summary */}
          <div>
            <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:14,padding:'18px 20px',boxShadow:T.shadow,marginBottom:14}}>
              <div style={{fontWeight:700,fontSize:14,color:T.text,marginBottom:12}}>Financial Overview</div>
              {[['Total Billed',fmtINR(client.total_billed),T.text],['Total Paid',fmtINR(client.total_paid),T.green],['Outstanding',fmtINR(client.total_outstanding),Number(client.total_outstanding)>0?T.red:T.green]].map(([l,v,c])=>(
                <div key={l} style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:`1px solid ${T.borderL}`,fontSize:14}}>
                  <span style={{color:T.textMd,fontWeight:500}}>{l}</span>
                  <span style={{fontWeight:700,color:c}}>{v}</span>
                </div>
              ))}
              <div style={{display:'flex',justifyContent:'space-between',padding:'8px 0',fontSize:14}}>
                <span style={{color:T.textMd,fontWeight:500}}>Health Score</span>
                <span style={{fontWeight:700,color:client.health_score>=70?T.green:T.red}}>{client.health_score}%</span>
              </div>
            </div>
            {/* Services */}
            <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:14,padding:'18px 20px',boxShadow:T.shadow}}>
              <div style={{fontWeight:700,fontSize:14,color:T.text,marginBottom:10}}>Package: {client.package_name}</div>
              {(client.services||[]).map((s,i)=>(
                <div key={i} style={{display:'flex',alignItems:'center',gap:8,padding:'5px 0',borderBottom:`1px solid ${T.borderL}`,fontSize:13}}>
                  <div style={{width:20,height:20,background:T.purple,borderRadius:'50%',color:'#fff',fontSize:9,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>{i+1}</div>
                  <span style={{color:T.textMd}}>{typeof s === 'string' ? s : (s.title||s.description||'')}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* INVOICES */}
      {activeTab==='invoices'&&(
        <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:14,overflow:'hidden',boxShadow:T.shadow}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 110px 110px 110px 110px',padding:'11px 18px',background:'#faf9ff',borderBottom:`1px solid ${T.borderL}`}}>
            {['Invoice No.','Invoice Date','Due Date','Amount',''].map(h=><span key={h} style={{fontWeight:600,fontSize:11,color:T.textSm,textTransform:'uppercase',letterSpacing:0.7}}>{h}</span>)}
          </div>
          {!billing?.invoices?.length?<div style={{padding:28,textAlign:'center',color:T.textSm,fontSize:14}}>No invoices yet</div>:(
            billing.invoices.map((inv,idx)=>(
              <div key={inv.id} style={{display:'grid',gridTemplateColumns:'1fr 110px 110px 110px 110px',padding:'12px 18px',borderBottom:idx<billing.invoices.length-1?`1px solid ${T.borderL}`:'none',alignItems:'center'}}>
                <div>
                  <div style={{fontWeight:600,fontSize:13,color:T.text}}>{inv.invoice_number}</div>
                  <div style={{fontSize:11,color:T.textXs,marginBottom:4}}>{inv.client_invoice_number}</div>
                  <StatusBadge status={inv.status}/>
                </div>
                <span style={{fontSize:13,color:T.textMd}}>{fmtDate(inv.invoice_date)}</span>
                <span style={{fontSize:13,color:T.textMd}}>{fmtDate(inv.due_date)||'—'}</span>
                <span style={{fontSize:13,fontWeight:600,color:T.text}}>{fmtINR(inv.total_amount)}</span>
<div style={{display:'flex',gap:4,alignItems:'center'}}>
  <button
    onClick={async()=>{const r=await api.get(`/invoices/${inv.id}/print`);generateInvoicePDF({...r.data,client});}}
    style={{background:T.purpleBg,border:`1px solid #c4b5fd`,color:T.purpleText,cursor:'pointer',borderRadius:7,width:30,height:30,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}
    title="Download PDF"
  ><Download size={13}/></button>

  <button
    onClick={()=>setEditInvoice(inv)}
    style={{background:T.yellowBg,border:`1px solid ${T.yellowBorder}`,color:T.yellow,cursor:'pointer',borderRadius:7,width:30,height:30,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}
    title="Edit Invoice Amount"
  ><Edit2 size={13}/></button>

  <button
    onClick={async()=>{
      if(!window.confirm(`Delete invoice ${inv.invoice_number}? This cannot be undone.`))return;
      try{
        await api.delete(`/invoices/${inv.id}`);
        toast.success('Invoice deleted');
        api.get(`/invoices/billing/${client.id}`).then(r=>setBilling(r.data));
        api.get(`/clients/${client.id}`).then(r=>setClient(r.data));
      }catch(e){toast.error(e.response?.data?.error||'Failed');}
    }}
    style={{background:'#fee2e2',border:'1px solid #fca5a5',color:T.red,cursor:'pointer',borderRadius:7,width:30,height:30,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}
    title="Delete Invoice"
  ><Trash2 size={13}/></button>
</div>
              </div>
            ))
          )}
        </div>
      )}

      {/* RECEIPTS */}
      {activeTab==='receipts'&&(
        <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:14,overflow:'hidden',boxShadow:T.shadow}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 100px 80px 90px 100px',padding:'11px 18px',background:'#faf9ff',borderBottom:`1px solid ${T.borderL}`}}>
            {['Receipt No.','Date','Received','Balance','Status',''].map(h=><span key={h} style={{fontWeight:600,fontSize:11,color:T.textSm,textTransform:'uppercase',letterSpacing:0.7}}>{h}</span>)}
          </div>
          {!billing?.receipts?.length?<div style={{padding:28,textAlign:'center',color:T.textSm,fontSize:14}}>No payments recorded yet</div>:(
            billing.receipts.map((rcp,idx)=>(
              <div key={rcp.id} style={{display:'grid',gridTemplateColumns:'1fr 1fr 100px 80px 90px 100px',padding:'12px 18px',borderBottom:idx<billing.receipts.length-1?`1px solid ${T.borderL}`:'none',alignItems:'center'}}>
                <div><div style={{fontWeight:600,fontSize:13,color:T.text}}>{rcp.receipt_number}</div><div style={{fontSize:11,color:T.textXs,textTransform:'capitalize'}}>{rcp.payment_mode}</div></div>
                <span style={{fontSize:13,color:T.textMd}}>{fmtDate(rcp.receipt_date)}</span>
                <span style={{fontSize:13,fontWeight:600,color:T.green}}>{fmtINR(rcp.amount_received)}</span>
                <span style={{fontSize:13,fontWeight:600,color:Number(rcp.balance_remaining)>0?T.red:T.green}}>{fmtINR(rcp.balance_remaining)}</span>
                <StatusBadge status={rcp.payment_status}/>

<div style={{display:'flex',gap:4,alignItems:'center'}}>
  <button
    onClick={async()=>{const r=await api.get(`/invoices/receipts/${rcp.id}/print`);generateReceiptPDF({...r.data,client});}}
    style={{background:T.greenBg,border:`1px solid ${T.greenBorder}`,color:T.green,cursor:'pointer',borderRadius:7,width:30,height:30,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}
    title="Download PDF"
  ><Download size={13}/></button>

  <button
    onClick={()=>setEditReceipt(rcp)}
    style={{background:T.yellowBg,border:`1px solid ${T.yellowBorder}`,color:T.yellow,cursor:'pointer',borderRadius:7,width:30,height:30,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}
    title="Edit Receipt"
  ><Edit2 size={13}/></button>

  <button
    onClick={async()=>{
      if(!window.confirm(`Delete receipt ${rcp.receipt_number}? This cannot be undone.`))return;
      try{
        await api.delete(`/invoices/receipts/${rcp.id}`);
        toast.success('Receipt deleted');
        api.get(`/invoices/billing/${client.id}`).then(r=>setBilling(r.data));
        api.get(`/clients/${client.id}`).then(r=>setClient(r.data));
      }catch(e){toast.error(e.response?.data?.error||'Failed');}
    }}
    style={{background:'#fee2e2',border:'1px solid #fca5a5',color:T.red,cursor:'pointer',borderRadius:7,width:30,height:30,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}
    title="Delete Receipt"
  ><Trash2 size={13}/></button>
</div>
              </div>
            ))
          )}
        </div>
      )}

      {/* BILLING HISTORY */}
      {activeTab==='billing'&&(
        <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:14,overflow:'hidden',boxShadow:T.shadow}}>
          <div style={{padding:'14px 18px',borderBottom:`1px solid ${T.borderL}`,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <span style={{fontWeight:700,fontSize:14,color:T.text}}>All Billing Cycles</span>
            <button onClick={()=>setShowNewCycle(true)}
              style={{background:T.purple,border:'none',color:'#fff',cursor:'pointer',borderRadius:9,padding:'7px 13px',fontSize:12,fontWeight:600,fontFamily:T.font,display:'flex',alignItems:'center',gap:5}}>
              <Plus size={12}/>New Billing Cycle
            </button>
          </div>
          {!billing?.cycles?.length?<div style={{padding:28,textAlign:'center',color:T.textSm,fontSize:14}}>No billing cycles</div>:(
            billing.cycles.map((c,idx)=>(
              <div key={c.id} style={{padding:'14px 18px',borderBottom:idx<billing.cycles.length-1?`1px solid ${T.borderL}`:'none'}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
                  <div><span style={{fontWeight:600,fontSize:13,color:T.text}}>Cycle {c.cycle_number}</span><span style={{marginLeft:8,fontSize:12,color:T.textSm}}>{fmtDate(c.cycle_start)} → {fmtDate(c.cycle_end)}</span></div>
                  <StatusBadge status={c.status}/>
                </div>
                <div style={{display:'flex',gap:16}}>
                  {[['Due',fmtINR(c.total_due),T.text],['Paid',fmtINR(c.total_paid),T.green],['Balance',fmtINR(c.balance_remaining),Number(c.balance_remaining)>0?T.red:T.green]].map(([l,v,col])=>(
                    <div key={l}><div style={{fontSize:11,color:T.textXs}}>{l}</div><div style={{fontSize:13,fontWeight:600,color:col}}>{v}</div></div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ACTIVITY LOG */}
      {activeTab==='activity'&&(
        <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:14,overflow:'hidden',boxShadow:T.shadow}}>
          {!activity.length?<div style={{padding:28,textAlign:'center',color:T.textSm,fontSize:14}}>No activity yet</div>:(
            activity.map((a,idx)=>(
              <div key={a.id} style={{display:'flex',gap:12,padding:'12px 18px',borderBottom:idx<activity.length-1?`1px solid ${T.borderL}`:'none'}}>
                <div style={{width:8,height:8,borderRadius:'50%',background:T.purple,marginTop:5,flexShrink:0}}/>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,color:T.text,fontWeight:500}}>{a.description}</div>
                  <div style={{fontSize:11,color:T.textXs,marginTop:2}}>by {a.user?.name||'Admin'} · {new Date(a.created_at).toLocaleString('en-IN')}</div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {showEdit&&<EditClientModal client={client} onClose={()=>setShowEdit(false)} onSaved={()=>{setShowEdit(false);api.get(`/clients/${client.id}`).then(r=>setClient(r.data));}}/>}
      {showInvoice&&<InvoiceModal client={client} onClose={()=>setShowInvoice(false)} onSaved={()=>{setShowInvoice(false);api.get(`/invoices/billing/${client.id}`).then(r=>setBilling(r.data));}}/>}
      {showReceipt&&<ReceiptModal client={client} onClose={()=>setShowReceipt(false)} onSaved={()=>{setShowReceipt(false);api.get(`/invoices/billing/${client.id}`).then(r=>setBilling(r.data));api.get(`/clients/${client.id}`).then(r=>setClient(r.data));}}/>}
      {showNewCycle&&<NewBillingCycleModal client={client} cycles={billing?.cycles||[]} onClose={()=>setShowNewCycle(false)} onSaved={()=>{setShowNewCycle(false);api.get(`/invoices/billing/${client.id}`).then(r=>setBilling(r.data));}}/>}
        {editInvoice && (
  <EditInvoiceModal
    invoice={editInvoice}
    client={client}
    onClose={() => setEditInvoice(null)}
    onSaved={() => {
      setEditInvoice(null);
      api.get(`/invoices/billing/${client.id}`).then(r => setBilling(r.data));
      api.get(`/clients/${client.id}`).then(r => setClient(r.data));
    }}
  />
)}
{editReceipt && (
  <EditReceiptModal
    receipt={editReceipt}
    client={client}
    onClose={() => setEditReceipt(null)}
    onSaved={() => {
      setEditReceipt(null);
      api.get(`/invoices/billing/${client.id}`).then(r => setBilling(r.data));
      api.get(`/clients/${client.id}`).then(r => setClient(r.data));
    }}
  />
)}
    </div>
  );
}

/* ─── REPORTS TAB ────────────────────────────────────────────── */
function ReportsTab() {
  const [data, setData] = useState(null);
  useEffect(()=>{ api.get('/clients/reports/overview').then(r=>setData(r.data)).catch(()=>{}); },[]);
  if (!data) return <Spinner/>;
  return (
    <div>
      <h2 style={{fontWeight:700,fontSize:20,color:T.text,marginBottom:20}}>Revenue & Reports</h2>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:14,marginBottom:28}}>
        <StatCard icon={Users}       label="Overall Clients"          value={data.total_clients}            color={T.purple} bg={T.purpleBg}/>
        <StatCard icon={FileText}    label="Total Invoices Generated"  value={data.total_invoices_generated} color={T.blue}   bg={T.blueBg}/>
        <StatCard icon={CheckCircle} label="Invoices Closed"           value={data.total_invoices_closed}    color={T.green}  bg={T.greenBg}/>
        <StatCard icon={AlertCircle} label="Invoices Pending"          value={data.total_invoices_pending}   color={T.red}    bg={T.redBg}/>
      </div>

    </div>
  );
}

/* ─── PREVIOUS CLIENTS TAB ───────────────────────────────────── */
function PreviousClientsTab({onRestore}) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get('/clients',{params:{status:'deleted'}}).then(r=>{setClients(r.data);setLoading(false);}).catch(()=>setLoading(false));
  };
  useEffect(()=>load(),[]);

  const restore = async (id, name) => {
    if (!window.confirm(`Restore ${name} to active clients?`)) return;
    await api.put(`/clients/${id}/restore`);
    toast.success(`${name} restored`);
    load(); onRestore();
  };

  if (loading) return <Spinner/>;

  return (
    <div>
      <h2 style={{fontWeight:700,fontSize:20,color:T.text,marginBottom:20}}>Previous Clients</h2>
      {!clients.length?(
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:300,gap:12}}>
          <div style={{width:70,height:70,background:'#f3f4f6',borderRadius:18,display:'flex',alignItems:'center',justifyContent:'center'}}><History size={30} color="#9ca3af"/></div>
          <div style={{fontWeight:600,fontSize:15,color:T.textSm}}>No previous clients</div>
        </div>
      ):(
        clients.map(c=>(
          <div key={c.id} style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:12,padding:'16px 18px',marginBottom:10,boxShadow:T.shadow}}>
            <div style={{display:'flex',alignItems:'flex-start',gap:12}}>
              <div style={{width:38,height:38,background:'#f3f4f6',borderRadius:10,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:14,color:'#6b7280'}}>{c.company_name?.[0]?.toUpperCase()}</div>
              <div style={{flex:1}}>
                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:3}}><span style={{fontWeight:600,fontSize:14,color:T.text}}>{c.company_name}</span><span style={{fontSize:11,color:T.textXs,background:'#f3f4f6',padding:'1px 7px',borderRadius:10}}>{c.client_code}</span></div>
                <div style={{fontSize:12,color:T.textSm,marginBottom:4}}>{c.contact_person} · {c.package_name}</div>
                {c.deleted_reason&&<div style={{fontSize:12,color:'#9a3412',background:'#fff7ed',padding:'4px 8px',borderRadius:6,display:'inline-block'}}>Reason: {c.deleted_reason}</div>}
                {c.deleted_at&&<div style={{fontSize:11,color:T.textXs,marginTop:4}}>Removed on {new Date(c.deleted_at).toLocaleDateString('en-IN')}</div>}
              </div>
              <div style={{display:'flex',gap:8}}>
                <div style={{textAlign:'right',marginRight:8}}>
                  <div style={{fontSize:12,color:T.green,fontWeight:600}}>{fmtINR(c.total_paid)} paid</div>
                  <div style={{fontSize:12,color:T.red}}>{fmtINR(c.total_outstanding)} outstanding</div>
                </div>
                <Btn variant="secondary" small onClick={()=>restore(c.id,c.company_name)}><RotateCcw size={11}/>Restore</Btn>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

/* ─── MAIN CLIENTS PAGE ──────────────────────────────────────── */
export default function ClientsPage() {
   const { user } = useAuth();              // ADD THIS LINE
  const isAdmin = user?.role === 'admin';  // ADD THIS LINE
  const [activeTab, setActiveTab] = useState('clients');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAdd, setShowAdd] = useState(false);
  const [viewClient, setViewClient] = useState(null);
  const [invoiceClient, setInvoiceClient] = useState(null);
  const [receiptClient, setReceiptClient] = useState(null);
  const [deleteClient, setDeleteClient] = useState(null);
  const [editClient, setEditClient] = useState(null);
  const [overview, setOverview] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/clients', { params: { status: 'active', search: search || undefined } })
      .then(r => setClients(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    api.get('/clients/reports/overview')
      .then(r => setOverview(r.data))
      .catch(() => {});
  }, [search]);

  useEffect(()=>{ load(); }, [load]);

  // Filter
  const filtered = clients.filter(c => {
    if (filterStatus === 'all') return true;
    const outstanding = Number(c.total_outstanding||0);
    const paid = Number(c.total_paid||0);
    const billed = Number(c.total_billed||0);
    if (filterStatus === 'paid') return outstanding === 0 && billed > 0;
    if (filterStatus === 'partial') return outstanding > 0 && paid > 0;
    if (filterStatus === 'unpaid') return outstanding > 0 && paid === 0;
    if (filterStatus === 'overdue') return c.current_cycle?.status === 'overdue';
    return true;
  });

  if (viewClient) return (
    <ClientDetail client={viewClient} onBack={()=>{setViewClient(null);load();}} onRefresh={load}/>
  );

  return (
    <div style={{fontFamily:T.font}}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Page header */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20,flexWrap:'wrap',gap:12}}>
        <div>
          <h1 style={{fontWeight:700,fontSize:24,color:T.text,letterSpacing:-0.3,marginBottom:3}}>Client Management</h1>
          <p style={{fontWeight:400,fontSize:14,color:T.textSm}}>Manage clients, invoices, receipts and billing</p>
        </div>
        {isAdmin && <Btn onClick={()=>setShowAdd(true)}><Plus size={14}/>Add Client</Btn>}
      </div>

      {/* Overview stat cards */}
      {isAdmin && overview&&(
        <div style={{display:'flex',gap:14,marginBottom:24,flexWrap:'wrap'}}>
         <StatCard icon={Users}       label="Overall Clients"          value={overview.total_clients}            color={T.purple} bg={T.purpleBg}/>
<StatCard icon={FileText}    label="Total Invoices Generated"  value={overview.total_invoices_generated} color={T.blue}   bg={T.blueBg}/>
<StatCard icon={CheckCircle} label="Invoices Closed"           value={overview.total_invoices_closed}    color={T.green}  bg={T.greenBg}/>
<StatCard icon={AlertCircle} label="Invoices Pending"          value={overview.total_invoices_pending}   color={T.red}    bg={T.redBg}/>
        </div>
      )}

      {/* Main tabs */}
      <div style={{display:'flex',gap:0,borderBottom:`2px solid ${T.borderL}`,marginBottom:22}}>
        {[{id:'clients',l:'Active Clients'},...(isAdmin?[{id:'previous',l:'Previous Clients'},{id:'reports',l:'Reports'}]:[])] .map(t=>(
          <button key={t.id} onClick={()=>setActiveTab(t.id)}
            style={{background:'none',border:'none',padding:'9px 18px',fontSize:14,fontWeight:activeTab===t.id?600:400,color:activeTab===t.id?T.purple:T.textSm,cursor:'pointer',fontFamily:T.font,borderBottom:activeTab===t.id?`2px solid ${T.purple}`:'2px solid transparent',marginBottom:-2}}>
            {t.l}
          </button>
        ))}
      </div>

      {/* ACTIVE CLIENTS */}
      {activeTab==='clients'&&(
        <>
          {/* Filters */}
          <div style={{display:'flex',gap:10,marginBottom:18,flexWrap:'wrap',alignItems:'center'}}>
            <div style={{display:'flex',alignItems:'center',gap:8,background:T.surface,border:`1px solid ${T.border}`,borderRadius:9,padding:'8px 13px',flex:1,minWidth:200}}>
              <Search size={14} color={T.textXs}/>
              <input style={{background:'transparent',border:'none',outline:'none',color:T.text,fontSize:13,flex:1,fontFamily:T.font}} placeholder="Search clients..." value={search} onChange={e=>setSearch(e.target.value)}/>
              {search&&<X size={13} color={T.textXs} style={{cursor:'pointer'}} onClick={()=>setSearch('')}/>}
            </div>
            <div style={{display:'flex',background:T.surface,border:`1px solid ${T.border}`,borderRadius:9,overflow:'hidden'}}>
              {[['all','All'],['paid','Paid'],['partial','Partial'],['unpaid','Unpaid'],['overdue','Overdue']].map(([v,l])=>(
                <button key={v} onClick={()=>setFilterStatus(v)}
                  style={{background:filterStatus===v?T.purpleBg:'transparent',border:'none',color:filterStatus===v?T.purpleText:T.textSm,cursor:'pointer',padding:'8px 13px',fontSize:12,fontWeight:filterStatus===v?600:400,fontFamily:T.font,transition:'all 0.15s'}}>
                  {l}
                </button>
              ))}
            </div>
            <Btn variant="ghost" onClick={load} small><RefreshCw size={13}/>Refresh</Btn>
          </div>

          {loading?<Spinner/>:filtered.length===0?(
            <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:300,gap:14}}>
              <div style={{width:70,height:70,background:T.purpleBg,borderRadius:18,display:'flex',alignItems:'center',justifyContent:'center'}}><Building2 size={30} color={T.purple}/></div>
              <div style={{fontWeight:600,fontSize:16,color:T.text,marginBottom:4}}>{search?'No clients found':'No clients yet'}</div>
              <div style={{fontSize:14,color:T.textSm}}>{search?'Try different search':'Add your first client to get started'}</div>
              {!search&&<Btn onClick={()=>setShowAdd(true)}><Plus size={14}/>Add First Client</Btn>}
            </div>
          ):(
            <div>
              <div style={{fontSize:13,color:T.textSm,marginBottom:12,fontWeight:500}}>{filtered.length} client{filtered.length!==1?'s':''}</div>
              {filtered.map(c=>(
                <ClientRow key={c.id} client={c}
                  onView={()=>setViewClient(c)}
                  onInvoice={()=>setInvoiceClient(c)}
                  onReceipt={()=>setReceiptClient(c)}
                  onEdit={()=>setEditClient(c)}
                  onDelete={()=>setDeleteClient(c)}
                  isAdmin={isAdmin} 
                />
              ))}
            </div>
          )}
        </>
      )}

      {activeTab==='previous'&&<PreviousClientsTab onRestore={load}/>}
      {activeTab==='reports'&&<ReportsTab/>}

      {/* Modals */}
      {showAdd&&<AddClientModal onClose={()=>setShowAdd(false)} onSaved={()=>{setShowAdd(false);load();}}/>}
      {editClient&&<EditClientModal client={editClient} onClose={()=>setEditClient(null)} onSaved={()=>{setEditClient(null);load();}}/>}
      {invoiceClient&&<InvoiceModal client={invoiceClient} onClose={()=>setInvoiceClient(null)} onSaved={()=>{setInvoiceClient(null);load();}}/>}
      {receiptClient&&<ReceiptModal client={receiptClient} onClose={()=>setReceiptClient(null)} onSaved={()=>{setReceiptClient(null);load();}}/>}
      {deleteClient&&<DeleteClientModal client={deleteClient} onClose={()=>setDeleteClient(null)} onDeleted={()=>{setDeleteClient(null);load();}}/>}
    </div>
  );
}